// ─────────────────────────────────────────────────────────────────
// Control Systems — Bode Plots
// ─────────────────────────────────────────────────────────────────

const S = {
  wrapper:      { marginTop:'10px', padding:'40px', border:'3px solid grey', fontFamily:"'Georgia', serif", color:'#1a1a2e', lineHeight:'1.7' },
  h1:           { fontSize:'2rem', fontWeight:'700', borderBottom:'3px solid #c0392b', paddingBottom:'8px', marginBottom:'24px', color:'#7B0D0D' },
  h2:           { fontSize:'1.3rem', fontWeight:'700', color:'#c0392b', marginTop:'36px', marginBottom:'12px', borderLeft:'4px solid #c0392b', paddingLeft:'10px' },
  p:            { marginBottom:'12px', fontSize:'0.97rem' },
  infoBox:      { background:'#FEF0F0', border:'2px solid #c0392b', borderRadius:'6px', padding:'16px 20px', margin:'16px 0' },
  infoBoxTitle: { fontWeight:'700', fontFamily:"'Courier New', monospace", fontSize:'0.88rem', color:'#7B0D0D', marginBottom:'8px' },
  infoBoxItem:  { fontSize:'0.9rem', margin:'4px 0', paddingLeft:'12px', borderLeft:'2px solid #c0392b' },
  table:        { width:'100%', borderCollapse:'collapse', marginBottom:'20px', fontSize:'0.92rem' },
  th:           { background:'#c0392b', color:'#fff', padding:'10px 14px', textAlign:'left', fontWeight:'700', fontSize:'0.9rem' },
  tdE:          { padding:'10px 14px', borderBottom:'1px solid #f0cece', verticalAlign:'top', background:'#fff5f5' },
  tdO:          { padding:'10px 14px', borderBottom:'1px solid #f0cece', verticalAlign:'top', background:'#fff' },
  tdL:          { fontWeight:'600', color:'#7B0D0D', whiteSpace:'nowrap', width:'220px' },
  code:         { fontFamily:"'Courier New', monospace", fontSize:'0.88rem', whiteSpace:'pre-wrap' },
  dWrap:        { margin:'24px 0', background:'#fffafa', border:'1px solid #f0cece', borderRadius:'8px', padding:'20px' },
  dTitle:       { fontWeight:'700', fontSize:'0.95rem', color:'#7B0D0D', marginBottom:'12px', textAlign:'center', letterSpacing:'0.02em' },
  dCaption:     { fontSize:'0.84rem', color:'#555', textAlign:'center', marginTop:'10px', fontStyle:'italic' },
};

function InfoBox({ title, items }) {
  return (
    <div style={S.infoBox}>
      <div style={S.infoBoxTitle}>{title}</div>
      {items.map((item, i) => <div key={i} style={S.infoBoxItem}>{item}</div>)}
    </div>
  );
}

function Table2({ rows }) {
  return (
    <table style={S.table}>
      <thead><tr>
        <th style={{ ...S.th, width:'220px' }}>Concept</th>
        <th style={S.th}>Explanation / Formula</th>
      </tr></thead>
      <tbody>
        {rows.map(([label, content], i) => (
          <tr key={i}>
            <td style={{ ...(i%2===0 ? S.tdE : S.tdO), ...S.tdL }}>{label}</td>
            <td style={{ ...(i%2===0 ? S.tdE : S.tdO), ...S.code }}>{content}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function Arr({ id, color }) {
  return (
    <marker id={id} markerWidth="8" markerHeight="8" refX="6" refY="3" orient="auto">
      <polygon points="0 0,7 3,0 6" fill={color}/>
    </marker>
  );
}

// ─────────────────────────────────────────────────────────────────
// Shared Bode helpers — all computed before any JSX
// ─────────────────────────────────────────────────────────────────
function logSpace(wMin, wMax, N) {
  const arr = [];
  for (let i = 0; i <= N; i++) arr.push(wMin * Math.pow(wMax / wMin, i / N));
  return arr;
}

function bodeAxes(ox, oy, PW, PH, wMin, wMax, magMin, magMax, wTicks, magTicks) {
  const swx = w   => ox + 36 + (Math.log10(w / wMin) / Math.log10(wMax / wMin)) * (PW - 52);
  const smy = dB  => oy + PH - 18 - ((dB - magMin) / (magMax - magMin)) * (PH - 28);
  return { swx, smy };
}

// ─────────────────────────────────────────────────────────────────
// Diagram 1 — Sinusoidal Steady-State: What Bode Plots Show
// ─────────────────────────────────────────────────────────────────
function DiagramSinusoidalSteadyState() {
  const W = 900, H = 380;

  // Compute sinusoidal input/output for G(s)=1/((s+1)(0.3s+1)) at two frequencies
  const dt = 0.01;
  const freqs = [
    { w: 0.5, color: '#2E75B6', label: 'ω=0.5 rad/s (below corner)' },
    { w: 3.0, color: '#c0392b', label: 'ω=3 rad/s (above corner)' },
  ];

  // For each frequency compute steady-state amplitude ratio and phase lag
  // G(jω) = 1/((jω+1)(0.3jω+1))
  const gFreqData = freqs.map(({ w, color, label }) => {
    const re1 = 1, im1 = w;        // (jω+1)
    const re2 = 1, im2 = 0.3 * w;  // (0.3jω+1)
    const dRe = re1 * re2 - im1 * im2;
    const dIm = re1 * im2 + im1 * re2;
    const mag = 1 / Math.sqrt(dRe * dRe + dIm * dIm);
    const phase = -Math.atan2(dIm, dRe);
    const mag_dB = 20 * Math.log10(mag);
    return { w, color, label, mag, phase, mag_dB };
  });

  // Build sinusoidal waveform data for two panels
  const tMax = 14;
  const N = 700;

  const waveData = gFreqData.map(({ w, mag, phase }) => {
    const pts_in = [], pts_out = [];
    for (let i = 0; i <= N; i++) {
      const t = i * tMax / N;
      pts_in.push({ t, y: Math.sin(w * t) });
      pts_out.push({ t, y: mag * Math.sin(w * t + phase) });
    }
    return { pts_in, pts_out };
  });

  const panelDefs = [
    { ox: 22,  label: freqs[0].label, color: freqs[0].color, data: waveData[0], gd: gFreqData[0] },
    { ox: 472, label: freqs[1].label, color: freqs[1].color, data: waveData[1], gd: gFreqData[1] },
  ];

  const PW = 410, PH = 200;

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Sinusoidal Steady-State Response — What Bode Plots Capture</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', display: 'block' }}>
        <defs>
          <Arr id="sss-blk" color="#aaa"/>
          {panelDefs.map(p => (
            <clipPath key={p.ox} id={`sss-c${p.ox}`}>
              <rect x={p.ox + 34} y={20} width={PW - 46} height={PH - 16}/>
            </clipPath>
          ))}
        </defs>

        {panelDefs.map(({ ox, label, color, data, gd }) => {
          const sx = t  => ox + 36 + (t / tMax) * (PW - 50);
          const sy = yv => 26 + PH / 2 - yv * 75;

          const inPath  = data.pts_in.map(({ t, y }, i) =>
            `${i === 0 ? 'M' : 'L'}${sx(t).toFixed(1)},${sy(y).toFixed(1)}`).join(' ');
          const outPath = data.pts_out.map(({ t, y }, i) =>
            `${i === 0 ? 'M' : 'L'}${sx(t).toFixed(1)},${sy(y).toFixed(1)}`).join(' ');

          return (
            <g key={ox}>
              <rect x={ox} y={18} width={PW} height={PH} rx={5} fill="#fff" stroke="#f0cece" strokeWidth="1.5"/>
              <text x={ox + PW / 2} y={12} textAnchor="middle" fontSize={11} fill="#7B0D0D" fontWeight="700">{label}</text>

              {/* Centre axis */}
              <line x1={ox + 34} y1={sy(0)} x2={ox + PW - 14} y2={sy(0)} stroke="#ddd" strokeWidth="1.2"/>
              <line x1={ox + 34} y1={18}    x2={ox + 34}       y2={18 + PH - 16} stroke="#bbb" strokeWidth="1"/>

              {/* Amplitude level lines */}
              <line x1={ox + 34} y1={sy(1)}       x2={ox + PW - 14} y2={sy(1)}       stroke="#f0f0f0" strokeWidth="1" strokeDasharray="3,3"/>
              <line x1={ox + 34} y1={sy(-1)}       x2={ox + PW - 14} y2={sy(-1)}     stroke="#f0f0f0" strokeWidth="1" strokeDasharray="3,3"/>
              <line x1={ox + 34} y1={sy(gd.mag)}   x2={ox + PW - 14} y2={sy(gd.mag)} stroke={color}   strokeWidth="1" strokeDasharray="4,3" opacity="0.5"/>
              <line x1={ox + 34} y1={sy(-gd.mag)}  x2={ox + PW - 14} y2={sy(-gd.mag)} stroke={color}  strokeWidth="1" strokeDasharray="4,3" opacity="0.5"/>

              {/* Waveforms */}
              <path d={inPath}  fill="none" stroke="#aaa"  strokeWidth="1.8" clipPath={`url(#sss-c${ox})`}/>
              <path d={outPath} fill="none" stroke={color} strokeWidth="2.5" clipPath={`url(#sss-c${ox})`}/>

              {/* Amplitude annotation */}
              <line x1={ox + PW - 28} y1={sy(0)} x2={ox + PW - 28} y2={sy(gd.mag)}
                stroke={color} strokeWidth="1.5" markerStart="url(#sss-blk)" markerEnd="url(#sss-blk)"/>
              <text x={ox + PW - 24} y={sy(gd.mag / 2) + 4} fontSize={9} fill={color} fontWeight="700">
                |G|={gd.mag.toFixed(3)}
              </text>
              <text x={ox + PW - 24} y={sy(gd.mag / 2) + 16} fontSize={8.5} fill={color}>
                ={gd.mag_dB.toFixed(1)}dB
              </text>

              {/* Phase annotation — arrow showing lag */}
              {(() => {
                const T = 2 * Math.PI / gd.w;
                const lagSec = -gd.phase / gd.w;
                const x1 = sx(T * 0.35);
                const x2 = sx(T * 0.35 + lagSec);
                const yLine = sy(-1.05);
                return (
                  <g>
                    <line x1={x1} y1={sy(0)} x2={x1} y2={yLine} stroke="#e07b00" strokeWidth="1" strokeDasharray="3,2"/>
                    <line x1={x2} y1={sy(0)} x2={x2} y2={yLine} stroke="#e07b00" strokeWidth="1" strokeDasharray="3,2"/>
                    <line x1={x1} y1={yLine + 4} x2={x2} y2={yLine + 4} stroke="#e07b00" strokeWidth="1.5"
                      markerStart="url(#sss-blk)" markerEnd="url(#sss-blk)"/>
                    <text x={(x1 + x2) / 2} y={yLine + 16} textAnchor="middle" fontSize={9} fill="#e07b00" fontWeight="700">
                      lag={(-gd.phase * 180 / Math.PI).toFixed(1)}°
                    </text>
                  </g>
                );
              })()}

              {/* Legend */}
              <line x1={ox + 38} y1={18 + PH - 10} x2={ox + 56} y2={18 + PH - 10} stroke="#aaa" strokeWidth="1.8"/>
              <text x={ox + 60}  y={18 + PH - 6}  fontSize={9} fill="#aaa">Input A·sin(ωt)</text>
              <line x1={ox + 160} y1={18 + PH - 10} x2={ox + 178} y2={18 + PH - 10} stroke={color} strokeWidth="2.5"/>
              <text x={ox + 182} y={18 + PH - 6}  fontSize={9} fill={color}>Output |G|·A·sin(ωt+∠G)</text>
            </g>
          );
        })}

        {/* Bottom: the Bode connection */}
        <rect x={10} y={236} width={880} height={136} rx={5} fill="#fff" stroke="#f0cece" strokeWidth="1.5"/>
        <text x={450} y={254} textAnchor="middle" fontSize={12} fill="#7B0D0D" fontWeight="700">
          The Bode Plot Captures This Behaviour Across All Frequencies
        </text>

        {/* Mini Bode diagram */}
        {(() => {
          const wMin = 0.1, wMax = 30, N = 150;
          const ws = logSpace(wMin, wMax, N);
          const magData = ws.map(w => {
            const re = 1 - 0.3 * w * w, im = w + 0.3 * w;
            return 20 * Math.log10(1 / Math.sqrt(re * re + im * im));
          });
          const phsData = ws.map(w => {
            const re = 1 - 0.3 * w * w, im = w + 0.3 * w;
            return -Math.atan2(im, re) * 180 / Math.PI;
          });

          const mox = 30, moy = 262, mPW = 400, mPH = 52;
          const pox = 30, poy = 320, pPH = 44;
          const swx = w  => mox + 20 + (Math.log10(w / wMin) / Math.log10(wMax / wMin)) * (mPW - 32);
          const smy = dB => moy + mPH - 8 - ((dB + 40) / 60) * (mPH - 14);
          const spy = ph => poy + pPH - 8 - ((ph + 200) / 200) * (pPH - 12);

          const magPath = magData.map((dB, i) =>
            `${i === 0 ? 'M' : 'L'}${swx(ws[i]).toFixed(1)},${smy(dB).toFixed(1)}`).join(' ');
          const phsPath = phsData.map((ph, i) =>
            `${i === 0 ? 'M' : 'L'}${swx(ws[i]).toFixed(1)},${spy(ph).toFixed(1)}`).join(' ');

          return (
            <g>
              <rect x={mox} y={moy} width={mPW} height={mPH} rx={3} fill="#fafafa" stroke="#ddd" strokeWidth="1"/>
              <rect x={pox} y={poy} width={mPW} height={pPH} rx={3} fill="#fafafa" stroke="#ddd" strokeWidth="1"/>
              <path d={magPath} fill="none" stroke="#c0392b" strokeWidth="2"/>
              <path d={phsPath} fill="none" stroke="#2E75B6" strokeWidth="2"/>
              <line x1={mox + 20} y1={smy(0)} x2={mox + mPW - 12} y2={smy(0)} stroke="#ddd" strokeWidth="1"/>
              <line x1={pox + 20} y1={spy(-90)} x2={pox + mPW - 12} y2={spy(-90)} stroke="#ddd" strokeWidth="1" strokeDasharray="3,2"/>
              <text x={mox + mPW / 2} y={moy - 4} textAnchor="middle" fontSize={9.5} fill="#c0392b" fontWeight="700">Magnitude [dB]</text>
              <text x={pox + mPW / 2} y={poy + pPH + 12} textAnchor="middle" fontSize={9.5} fill="#2E75B6" fontWeight="700">Phase [°]</text>

              {/* Mark the two test frequencies */}
              {gFreqData.map(({ w, color, mag_dB, phase }) => (
                <g key={w}>
                  <circle cx={swx(w)} cy={smy(mag_dB)}  r={5} fill={color} stroke="#fff" strokeWidth="1.5"/>
                  <circle cx={swx(w)} cy={spy(phase * 180 / Math.PI)} r={5} fill={color} stroke="#fff" strokeWidth="1.5"/>
                  <line x1={swx(w)} y1={moy} x2={swx(w)} y2={poy + pPH} stroke={color} strokeWidth="1" strokeDasharray="3,2" opacity="0.6"/>
                </g>
              ))}
            </g>
          );
        })()}

        {/* Explanation text */}
        <text x={460} y={274} fontSize={10}   fill="#7B0D0D" fontWeight="700">For each frequency ω, the Bode plot records two numbers:</text>
        <text x={460} y={291} fontSize={10}   fill="#c0392b">① Magnitude (dB) = 20·log₁₀(|G(jω)|)  — how much the amplitude is scaled</text>
        <text x={460} y={307} fontSize={10}   fill="#2E75B6">② Phase (degrees) = ∠G(jω)  — how much the output lags behind the input</text>
        <text x={460} y={324} fontSize={9.5}  fill="#555">The log frequency axis spans many decades, revealing behaviour at very different timescales.</text>
        <text x={460} y={340} fontSize={9.5}  fill="#555">Coloured dots on the Bode plot correspond to the two test frequencies shown in the panels above.</text>
        <text x={460} y={356} fontSize={9.5}  fill="#333" fontFamily="'Courier New',monospace">G(s) = 1/((s+1)(0.3s+1))  |  G(j0.5): {gFreqData[0].mag_dB.toFixed(1)}dB, {(gFreqData[0].phase*180/Math.PI).toFixed(1)}°  |  G(j3): {gFreqData[1].mag_dB.toFixed(1)}dB, {(gFreqData[1].phase*180/Math.PI).toFixed(1)}°</text>
        <text x={460} y={368} fontSize={9}    fill="#555">Coloured markers on Bode: low-ω (blue circle) and high-ω (red circle)</text>
      </svg>
      <div style={S.dCaption}>At each frequency, a sinusoidal input produces a sinusoidal output with a different amplitude and phase. The Bode plot is simply a systematic record of this amplitude ratio (in dB) and phase lag across all frequencies. At ω=0.5 rad/s (below corner), the gain is nearly 0 dB and lag is small. At ω=3 rad/s (above corner), the gain drops significantly and the phase lag grows — both captured as points on the Bode plot.</div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// Diagram 2 — Bode Axis Conventions & Log Scale
// ─────────────────────────────────────────────────────────────────
function DiagramBodeAxes() {
  const W = 900, H = 370;

  // Compute a sample Bode for illustration
  const wMin = 0.01, wMax = 1000, Npts = 200;
  const ws = logSpace(wMin, wMax, Npts);

  // G(s) = 10 / (s/0.1+1)(s/5+1)(s/50+1)
  const magData = ws.map(w => {
    const m1 = 1 / Math.sqrt(1 + (w / 0.1) ** 2);
    const m2 = 1 / Math.sqrt(1 + (w / 5) ** 2);
    const m3 = 1 / Math.sqrt(1 + (w / 50) ** 2);
    return 20 * Math.log10(10 * m1 * m2 * m3);
  });
  const phsData = ws.map(w => {
    return -(Math.atan(w / 0.1) + Math.atan(w / 5) + Math.atan(w / 50)) * 180 / Math.PI;
  });

  const PW = 400, PH = 140;
  const mOx = 34, mOy = 28, pOx = 34, pOy = 192;
  const swx = w   => mOx + 40 + (Math.log10(w / wMin) / Math.log10(wMax / wMin)) * (PW - 54);
  const smy = dB  => mOy + PH - 18 - ((dB + 60) / 100) * (PH - 28);
  const spy = ph  => pOy + PH - 18 - ((ph + 270) / 270) * (PH - 28);

  const magPath = magData.map((dB, i) =>
    `${i === 0 ? 'M' : 'L'}${swx(ws[i]).toFixed(1)},${Math.max(mOy + 4, Math.min(mOy + PH - 4, smy(dB))).toFixed(1)}`).join(' ');
  const phsPath = phsData.map((ph, i) =>
    `${i === 0 ? 'M' : 'L'}${swx(ws[i]).toFixed(1)},${Math.max(pOy + 4, Math.min(pOy + PH - 4, spy(ph))).toFixed(1)}`).join(' ');

  const wTicks = [0.01, 0.1, 1, 10, 100, 1000];
  const magTicks = [40, 20, 0, -20, -40, -60];
  const phsTicks = [0, -90, -180, -270];

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Bode Plot Axes — Conventions, Log Scale, and Reading the Plot</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', display: 'block' }}>
        <defs>
          <Arr id="ba-blk" color="#aaa"/>
          <clipPath id="ba-m"><rect x={mOx + 38} y={mOy + 4} width={PW - 50} height={PH - 22}/></clipPath>
          <clipPath id="ba-p"><rect x={pOx + 38} y={pOy + 4} width={PW - 50} height={PH - 22}/></clipPath>
        </defs>

        {/* ── LEFT: Magnitude plot ── */}
        <rect x={mOx} y={mOy} width={PW} height={PH} rx={4} fill="#fff" stroke="#f0cece" strokeWidth="1.5"/>
        <text x={mOx + PW / 2} y={mOy - 8} textAnchor="middle" fontSize={12} fill="#7B0D0D" fontWeight="700">
          Magnitude Plot — |G(jω)| in Decibels
        </text>

        {wTicks.map(wv => (
          <g key={wv}>
            <line x1={swx(wv)} y1={mOy + 6} x2={swx(wv)} y2={mOy + PH - 16} stroke="#f0f0f0" strokeWidth="1"/>
            <text x={swx(wv)} y={mOy + PH - 4} textAnchor="middle" fontSize={9} fill="#bbb">{wv}</text>
          </g>
        ))}
        {magTicks.map(mv => (
          <g key={mv}>
            <line x1={mOx + 38} y1={smy(mv)} x2={mOx + PW - 14} y2={smy(mv)} stroke="#f0f0f0" strokeWidth="1"/>
            <text x={mOx + 34} y={smy(mv) + 3} textAnchor="end" fontSize={9} fill="#bbb">{mv}</text>
          </g>
        ))}
        <line x1={mOx + 38} y1={smy(0)} x2={mOx + PW - 14} y2={smy(0)} stroke="#aaa" strokeWidth="1" strokeDasharray="4,3"/>
        <line x1={mOx + 38} y1={mOy + PH - 16} x2={mOx + PW - 10} y2={mOy + PH - 16} stroke="#aaa" strokeWidth="1.3" markerEnd="url(#ba-blk)"/>
        <line x1={mOx + 38} y1={mOy + PH - 16} x2={mOx + 38}       y2={mOy + 6}       stroke="#aaa" strokeWidth="1.3" markerEnd="url(#ba-blk)"/>

        <path d={magPath} fill="none" stroke="#c0392b" strokeWidth="2.5" clipPath="url(#ba-m)"/>

        {/* Annotations — axes */}
        <text x={mOx + PW - 8} y={mOy + PH - 8}  fontSize={9} fill="#555" fontStyle="italic">ω [rad/s]</text>
        <text x={mOx + 36}     y={mOy + 4}         fontSize={9} fill="#c0392b" fontStyle="italic">dB</text>

        {/* Phase plot */}
        <rect x={pOx} y={pOy} width={PW} height={PH} rx={4} fill="#fff" stroke="#f0cece" strokeWidth="1.5"/>
        <text x={pOx + PW / 2} y={pOy - 8} textAnchor="middle" fontSize={12} fill="#7B0D0D" fontWeight="700">
          Phase Plot — ∠G(jω) in Degrees
        </text>

        {wTicks.map(wv => (
          <g key={wv}>
            <line x1={swx(wv)} y1={pOy + 6} x2={swx(wv)} y2={pOy + PH - 16} stroke="#f0f0f0" strokeWidth="1"/>
            <text x={swx(wv)} y={pOy + PH - 4} textAnchor="middle" fontSize={9} fill="#bbb">{wv}</text>
          </g>
        ))}
        {phsTicks.map(pv => (
          <g key={pv}>
            <line x1={pOx + 38} y1={spy(pv)} x2={pOx + PW - 14} y2={spy(pv)} stroke="#f0f0f0" strokeWidth="1"/>
            <text x={pOx + 34} y={spy(pv) + 3} textAnchor="end" fontSize={9} fill="#bbb">{pv}°</text>
          </g>
        ))}
        <line x1={pOx + 38} y1={spy(-180)} x2={pOx + PW - 14} y2={spy(-180)} stroke="#c0392b" strokeWidth="1" strokeDasharray="5,3" opacity="0.5"/>
        <text x={pOx + 42} y={spy(-180) - 4} fontSize={8.5} fill="#c0392b" opacity="0.7">−180° (stability boundary)</text>
        <line x1={pOx + 38} y1={pOy + PH - 16} x2={pOx + PW - 10} y2={pOy + PH - 16} stroke="#aaa" strokeWidth="1.3" markerEnd="url(#ba-blk)"/>
        <line x1={pOx + 38} y1={pOy + PH - 16} x2={pOx + 38}       y2={pOy + 6}       stroke="#aaa" strokeWidth="1.3" markerEnd="url(#ba-blk)"/>

        <path d={phsPath} fill="none" stroke="#2E75B6" strokeWidth="2.5" clipPath="url(#ba-p)"/>
        <text x={pOx + PW - 8} y={pOy + PH - 8} fontSize={9} fill="#555" fontStyle="italic">ω [rad/s]</text>
        <text x={pOx + 36}     y={pOy + 4}        fontSize={9} fill="#2E75B6" fontStyle="italic">°</text>

        {/* ── RIGHT: Annotation panels ── */}
        <rect x={452} y={mOy} width={438} height={350} rx={5} fill="#fff" stroke="#f0cece" strokeWidth="1.5"/>
        <text x={671} y={mOy + 18} textAnchor="middle" fontSize={12} fill="#7B0D0D" fontWeight="700">Key Conventions &amp; Reading Rules</text>

        {[
          { y: mOy + 28, fill: '#FEF0F0', bc: '#c0392b',
            title: 'Magnitude (dB) scale',
            lines: ['20 dB = factor of 10 in amplitude',
                    '6 dB ≈ factor of 2  |  0 dB = gain of 1',
                    '−20 dB = factor of 0.1  |  −40 dB = 0.01',
                    'Slope: −20 dB/decade = one pole effect'] },
          { y: mOy + 104, fill: '#eaf3ff', bc: '#2E75B6',
            title: 'Log frequency axis',
            lines: ['Horizontal axis: log₁₀(ω) — NOT linear',
                    'One decade: ω to 10ω (e.g. 1→10, 10→100)',
                    'Equal spacing per decade regardless of magnitude',
                    'Typical range: 0.01 to 1000 rad/s'] },
          { y: mOy + 180, fill: '#f0fff4', bc: '#27ae60',
            title: 'Phase (degrees) scale',
            lines: ['Phase of 0°: output in phase with input',
                    'Phase of −90°: output lags by quarter cycle',
                    'Phase of −180°: output exactly anti-phase',
                    'Critical: −180° is stability boundary for FB'] },
          { y: mOy + 256, fill: '#fff5e6', bc: '#e07b00',
            title: 'Decibel arithmetic',
            lines: ['G₁G₂: add magnitudes (dB) and phases (°)',
                    '1/G: negate magnitude and phase',
                    'G^n: multiply magnitude and phase by n',
                    'This is WHY dB/log scale is so convenient'] },
        ].map(({ y, fill, bc, title, lines }) => (
          <g key={y}>
            <rect x={460} y={y} width={422} height={lines.length * 17 + 22} rx={3} fill={fill} stroke={bc} strokeWidth="1"/>
            <text x={468} y={y + 14} fontSize={10} fill="#7B0D0D" fontWeight="700">{title}</text>
            {lines.map((l, j) => (
              <text key={j} x={468} y={y + 29 + j * 16} fontSize={9.5} fill="#333">▸ {l}</text>
            ))}
          </g>
        ))}
      </svg>
      <div style={S.dCaption}>The Bode plot uses two key conventions that make it powerful: logarithmic frequency axis (compressing the wide range of frequencies into equal decades) and decibel magnitude scale (so products of transfer functions become sums). The phase always plots in degrees. The −180° phase line is the critical stability boundary — any gain crossover below −180° means the feedback loop is stable.</div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// Diagram 3 — First-Order Pole and Zero
// ─────────────────────────────────────────────────────────────────
function DiagramFirstOrder() {
  const W = 900, H = 380;

  const wMin = 0.01, wMax = 1000, Npts = 200;
  const tau = 1.0; // corner at ω = 1/τ = 1 rad/s
  const ws  = logSpace(wMin, wMax, Npts);

  // Pole: 1/(τs+1)
  const poleMag  = ws.map(w => 20 * Math.log10(1 / Math.sqrt(1 + (w * tau) ** 2)));
  const polePhs  = ws.map(w => -Math.atan(w * tau) * 180 / Math.PI);
  // Asymptote for pole magnitude
  const poleAsym = ws.map(w => w <= 1 / tau ? 0 : -20 * Math.log10(w * tau));
  // Zero: (τs+1)
  const zeroMag  = ws.map(w => 20 * Math.log10(Math.sqrt(1 + (w * tau) ** 2)));
  const zeroPhs  = ws.map(w =>  Math.atan(w * tau) * 180 / Math.PI);
  const zeroAsym = ws.map(w => w <= 1 / tau ? 0 :  20 * Math.log10(w * tau));

  const PW = 390, PH = 140;
  const mOx = 22, mOy = 28, pOx = 22, pOy = 188;
  const swx = w   => mOx + 36 + (Math.log10(w / wMin) / Math.log10(wMax / wMin)) * (PW - 48);
  const smy = dB  => mOy + PH - 18 - ((dB + 60) / 80) * (PH - 28);
  const spy = ph  => pOy + PH - 18 - ((ph + 100) / 200) * (PH - 28);

  const mkPath = (data, sx, sy) => data.map((v, i) =>
    `${i === 0 ? 'M' : 'L'}${sx(ws[i]).toFixed(1)},${Math.max(mOy + 2, Math.min(mOy + PH, sy(v))).toFixed(1)}`).join(' ');

  const wTicks = [0.01, 0.1, 1, 10, 100, 1000];

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>First-Order Pole &amp; Zero — Exact Curves vs Straight-Line Asymptotes</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', display: 'block' }}>
        <defs>
          <Arr id="fo-blk" color="#aaa"/>
          <clipPath id="fo-m"><rect x={mOx + 34} y={mOy + 4} width={PW - 44} height={PH - 20}/></clipPath>
          <clipPath id="fo-p"><rect x={pOx + 34} y={pOy + 4} width={PW - 44} height={PH - 20}/></clipPath>
        </defs>

        {/* Magnitude panel */}
        <rect x={mOx} y={mOy} width={PW} height={PH} rx={4} fill="#fff" stroke="#f0cece" strokeWidth="1.5"/>
        <text x={mOx + PW / 2} y={mOy - 8} textAnchor="middle" fontSize={11} fill="#7B0D0D" fontWeight="700">Magnitude [dB]</text>

        {wTicks.map(wv => (
          <g key={wv}>
            <line x1={swx(wv)} y1={mOy + 6} x2={swx(wv)} y2={mOy + PH - 16} stroke="#f5f5f5" strokeWidth="1"/>
            <text x={swx(wv)} y={mOy + PH - 4} textAnchor="middle" fontSize={8.5} fill="#ccc">{wv}</text>
          </g>
        ))}
        {[-60, -40, -20, 0, 20, 40].map(mv => (
          <g key={mv}>
            <line x1={mOx + 34} y1={smy(mv)} x2={mOx + PW - 12} y2={smy(mv)} stroke="#f5f5f5" strokeWidth="1"/>
            <text x={mOx + 30} y={smy(mv) + 3} textAnchor="end" fontSize={8.5} fill="#ccc">{mv}</text>
          </g>
        ))}
        <line x1={mOx + 34} y1={smy(0)} x2={mOx + PW - 12} y2={smy(0)} stroke="#aaa" strokeWidth="1" strokeDasharray="4,3"/>
        <line x1={mOx + 34} y1={mOy + PH - 16} x2={mOx + PW - 8} y2={mOy + PH - 16} stroke="#aaa" strokeWidth="1.3" markerEnd="url(#fo-blk)"/>
        <line x1={mOx + 34} y1={mOy + PH - 16} x2={mOx + 34}     y2={mOy + 6}       stroke="#aaa" strokeWidth="1.3" markerEnd="url(#fo-blk)"/>

        {/* Asymptotes */}
        <path d={mkPath(poleAsym, swx, smy)} fill="none" stroke="#c0392b" strokeWidth="1.5" strokeDasharray="8,4" clipPath="url(#fo-m)"/>
        <path d={mkPath(zeroAsym, swx, smy)} fill="none" stroke="#2E75B6" strokeWidth="1.5" strokeDasharray="8,4" clipPath="url(#fo-m)"/>
        {/* Exact */}
        <path d={mkPath(poleMag, swx, smy)} fill="none" stroke="#c0392b" strokeWidth="2.5" clipPath="url(#fo-m)"/>
        <path d={mkPath(zeroMag, swx, smy)} fill="none" stroke="#2E75B6" strokeWidth="2.5" clipPath="url(#fo-m)"/>

        {/* Corner frequency annotation */}
        <line x1={swx(1)} y1={mOy + 6} x2={swx(1)} y2={mOy + PH - 16} stroke="#e07b00" strokeWidth="1.5" strokeDasharray="4,3"/>
        <text x={swx(1)} y={mOy + PH - 4} textAnchor="middle" fontSize={8.5} fill="#e07b00" fontWeight="700">ωc=1/τ=1</text>
        <circle cx={swx(1)} cy={smy(-3)} r={4} fill="#c0392b" stroke="#fff" strokeWidth="1.5"/>
        <text x={swx(1) + 6} y={smy(-3) - 4} fontSize={8} fill="#c0392b">−3 dB</text>
        <circle cx={swx(1)} cy={smy(3)}  r={4} fill="#2E75B6" stroke="#fff" strokeWidth="1.5"/>
        <text x={swx(1) + 6} y={smy(3)  + 4} fontSize={8} fill="#2E75B6">+3 dB</text>
        <text x={swx(10) - 10} y={smy(-18)} fontSize={8.5} fill="#c0392b" fontStyle="italic">−20 dB/dec</text>
        <text x={swx(10) - 10} y={smy(18)} fontSize={8.5} fill="#2E75B6" fontStyle="italic">+20 dB/dec</text>

        {/* Phase panel */}
        <rect x={pOx} y={pOy} width={PW} height={PH} rx={4} fill="#fff" stroke="#f0cece" strokeWidth="1.5"/>
        <text x={pOx + PW / 2} y={pOy - 8} textAnchor="middle" fontSize={11} fill="#7B0D0D" fontWeight="700">Phase [°]</text>

        {wTicks.map(wv => (
          <line key={wv} x1={swx(wv)} y1={pOy + 6} x2={swx(wv)} y2={pOy + PH - 16} stroke="#f5f5f5" strokeWidth="1"/>
        ))}
        {[-90, -45, 0, 45, 90].map(pv => (
          <g key={pv}>
            <line x1={pOx + 34} y1={spy(pv)} x2={pOx + PW - 12} y2={spy(pv)} stroke="#f5f5f5" strokeWidth="1"/>
            <text x={pOx + 30} y={spy(pv) + 3} textAnchor="end" fontSize={8.5} fill="#ccc">{pv}°</text>
          </g>
        ))}
        <line x1={pOx + 34} y1={pOy + PH - 16} x2={pOx + PW - 8} y2={pOy + PH - 16} stroke="#aaa" strokeWidth="1.3" markerEnd="url(#fo-blk)"/>
        <line x1={pOx + 34} y1={pOy + PH - 16} x2={pOx + 34}     y2={pOy + 6}       stroke="#aaa" strokeWidth="1.3" markerEnd="url(#fo-blk)"/>

        {/* Phase asymptote (piecewise linear) */}
        {(() => {
          const pAsym = ws.map(w => {
            if (w <= 0.1) return 0;
            if (w >= 10)  return -90;
            return -45 * Math.log10(w / 0.1) * (1 / Math.log10(100));
          });
          const zAsym = ws.map(w => {
            if (w <= 0.1) return 0;
            if (w >= 10)  return 90;
            return 45 * Math.log10(w / 0.1) * (1 / Math.log10(100));
          });
          const mkPhs = (data) => data.map((v, i) =>
            `${i === 0 ? 'M' : 'L'}${swx(ws[i]).toFixed(1)},${spy(v).toFixed(1)}`).join(' ');
          return (
            <>
              <path d={mkPhs(pAsym)} fill="none" stroke="#c0392b" strokeWidth="1.5" strokeDasharray="8,4" clipPath="url(#fo-p)"/>
              <path d={mkPhs(zAsym)} fill="none" stroke="#2E75B6" strokeWidth="1.5" strokeDasharray="8,4" clipPath="url(#fo-p)"/>
            </>
          );
        })()}
        <path d={polePhs.map((v, i) => `${i === 0 ? 'M' : 'L'}${swx(ws[i]).toFixed(1)},${spy(v).toFixed(1)}`).join(' ')}
          fill="none" stroke="#c0392b" strokeWidth="2.5" clipPath="url(#fo-p)"/>
        <path d={zeroPhs.map((v, i) => `${i === 0 ? 'M' : 'L'}${swx(ws[i]).toFixed(1)},${spy(v).toFixed(1)}`).join(' ')}
          fill="none" stroke="#2E75B6" strokeWidth="2.5" clipPath="url(#fo-p)"/>

        <circle cx={swx(1)} cy={spy(-45)} r={4} fill="#c0392b" stroke="#fff" strokeWidth="1.5"/>
        <text x={swx(1) + 6} y={spy(-45)} fontSize={8} fill="#c0392b">−45° at ωc</text>
        <circle cx={swx(1)} cy={spy(45)}  r={4} fill="#2E75B6" stroke="#fff" strokeWidth="1.5"/>
        <text x={swx(1) + 6} y={spy(45)}  fontSize={8} fill="#2E75B6">+45° at ωc</text>

        <text x={pOx + 36} y={pOy + PH - 4} fontSize={8.5} fill="#555">ω [rad/s]</text>

        {/* RIGHT: Properties panel */}
        <rect x={430} y={mOy} width={460} height={350} rx={5} fill="#fff" stroke="#f0cece" strokeWidth="1.5"/>
        <text x={660} y={mOy + 18} textAnchor="middle" fontSize={12} fill="#7B0D0D" fontWeight="700">First-Order Bode Properties</text>

        {[
          { y: mOy + 28, fill: '#FEF0F0', bc: '#c0392b',
            title: 'Pole: 1/(τs+1)',
            lines: ['Corner freq: ωc = 1/τ',
                    'Magnitude: 0 dB (ω≪ωc) → −20dB/dec (ω≫ωc)',
                    'Exact at ωc: −3 dB (factor of 1/√2)',
                    'Phase: 0° → −90° (−45° at ωc exactly)',
                    'Asymptote error: ≤3 dB in magnitude, ≤6° in phase'] },
          { y: mOy + 130, fill: '#eaf3ff', bc: '#2E75B6',
            title: 'Zero: (τs+1)',
            lines: ['Corner freq: ωc = 1/τ  (same as pole)',
                    'Magnitude: 0 dB (ω≪ωc) → +20 dB/dec (ω≫ωc)',
                    'Exact at ωc: +3 dB (factor of √2)',
                    'Phase: 0° → +90° (+45° at ωc exactly)',
                    'Mirror image of pole in magnitude and phase'] },
          { y: mOy + 230, fill: '#f0fff4', bc: '#27ae60',
            title: 'Asymptote construction rules',
            lines: ['Draw flat line at DC gain (0 dB for unity)',
                    'At each corner frequency: break slope by ±20 dB/dec',
                    'Each pole: slope changes −20 dB/dec at ωc',
                    'Each zero: slope changes +20 dB/dec at ωc',
                    'Phase: linear approx. over one decade each side of ωc'] },
          { y: mOy + 322, fill: '#fff5e6', bc: '#e07b00',
            title: 'Exact correction at corner',
            lines: ['At ωc: exact = asymptote ± 3 dB (pole: −3, zero: +3)',
                    'One decade above/below: ±0.97 dB correction'] },
        ].map(({ y, fill, bc, title, lines }) => (
          <g key={y}>
            <rect x={438} y={y} width={444} height={lines.length * 17 + 20} rx={3} fill={fill} stroke={bc} strokeWidth="1"/>
            <text x={446} y={y + 14} fontSize={10} fill="#7B0D0D" fontWeight="700">{title}</text>
            {lines.map((l, j) => (
              <text key={j} x={446} y={y + 28 + j * 16} fontSize={9} fill="#333">▸ {l}</text>
            ))}
          </g>
        ))}
      </svg>
      <div style={S.dCaption}>The first-order pole 1/(τs+1) has a corner at ωc=1/τ. Below ωc the magnitude is flat (0 dB); above it rolls off at −20 dB/decade. The exact curve is 3 dB below the asymptote at ωc. The zero (τs+1) is the mirror image — +20 dB/decade above ωc and +3 dB at the corner. The straight-line (asymptote) approximation is accurate to within 3 dB everywhere and makes hand-sketching Bode plots straightforward.</div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// Diagram 4 — Second-Order Bode: Varying Damping Ratio
// ─────────────────────────────────────────────────────────────────
function DiagramSecondOrder() {
  const W = 900, H = 380;

  const wMin = 0.05, wMax = 20, Npts = 200;
  const wn = 1.0;
  const ws = logSpace(wMin, wMax, Npts);

  const zetaVals  = [0.1, 0.2, 0.3, 0.5, 0.707, 1.0, 2.0];
  const zColors   = ['#c0392b', '#e07b00', '#9b59b6', '#2E75B6', '#27ae60', '#555', '#aaa'];

  // G(s) = wn² / (s²+2ζwn·s+wn²)
  const allMag = zetaVals.map(z =>
    ws.map(w => {
      const u = w / wn;
      return 20 * Math.log10(1 / Math.sqrt((1 - u * u) ** 2 + (2 * z * u) ** 2));
    })
  );
  const allPhs = zetaVals.map(z =>
    ws.map(w => {
      const u = w / wn;
      return -Math.atan2(2 * z * u, 1 - u * u) * 180 / Math.PI;
    })
  );
  // Asymptote
  const asymMag = ws.map(w => w <= wn ? 0 : -40 * Math.log10(w / wn));

  const PW = 400, PH = 140;
  const mOx = 22, mOy = 28, pOx = 22, pOy = 192;
  const swx = w   => mOx + 36 + (Math.log10(w / wMin) / Math.log10(wMax / wMin)) * (PW - 48);
  const smy = dB  => mOy + PH - 18 - ((dB + 60) / 80) * (PH - 28);
  const spy = ph  => pOy + PH - 18 - ((ph + 200) / 200) * (PH - 28);

  const mkPath = (data, sy) => data.map((v, i) =>
    `${i === 0 ? 'M' : 'L'}${swx(ws[i]).toFixed(1)},${Math.max(mOy, Math.min(mOy + PH, sy(v))).toFixed(1)}`).join(' ');

  const wTicks = [0.05, 0.1, 0.2, 0.5, 1, 2, 5, 10, 20];

  // Resonant peak formulas
  const resPeaks = zetaVals.filter(z => z < Math.SQRT1_2).map(z => {
    const wr = wn * Math.sqrt(1 - 2 * z * z);
    const Mr = 1 / (2 * z * Math.sqrt(1 - z * z));
    const Mr_dB = 20 * Math.log10(Mr);
    return { z, wr, Mr, Mr_dB };
  });

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Second-Order System Bode — Resonance, Damping &amp; −40 dB/decade Roll-off</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', display: 'block' }}>
        <defs>
          <Arr id="so-blk" color="#aaa"/>
          <clipPath id="so-m"><rect x={mOx + 34} y={mOy + 4} width={PW - 44} height={PH - 20}/></clipPath>
          <clipPath id="so-p"><rect x={pOx + 34} y={pOy + 4} width={PW - 44} height={PH - 20}/></clipPath>
        </defs>

        {/* Magnitude */}
        <rect x={mOx} y={mOy} width={PW} height={PH} rx={4} fill="#fff" stroke="#f0cece" strokeWidth="1.5"/>
        <text x={mOx + PW / 2} y={mOy - 8} textAnchor="middle" fontSize={11} fill="#7B0D0D" fontWeight="700">
          Magnitude — G(s)=ωₙ²/(s²+2ζωₙs+ωₙ²), ωₙ=1
        </text>
        {wTicks.map(wv => (
          <g key={wv}>
            <line x1={swx(wv)} y1={mOy + 6} x2={swx(wv)} y2={mOy + PH - 16} stroke="#f5f5f5" strokeWidth="1"/>
            <text x={swx(wv)} y={mOy + PH - 4} textAnchor="middle" fontSize={8} fill="#ccc">{wv}</text>
          </g>
        ))}
        {[-60, -40, -20, 0, 20].map(mv => (
          <g key={mv}>
            <line x1={mOx + 34} y1={smy(mv)} x2={mOx + PW - 12} y2={smy(mv)} stroke="#f5f5f5" strokeWidth="1"/>
            <text x={mOx + 30} y={smy(mv) + 3} textAnchor="end" fontSize={8.5} fill="#ccc">{mv}</text>
          </g>
        ))}
        <line x1={mOx + 34} y1={smy(0)} x2={mOx + PW - 12} y2={smy(0)} stroke="#aaa" strokeWidth="1" strokeDasharray="4,3"/>
        <line x1={mOx + 34} y1={mOy + PH - 16} x2={mOx + PW - 8} y2={mOy + PH - 16} stroke="#aaa" strokeWidth="1.2" markerEnd="url(#so-blk)"/>
        <line x1={mOx + 34} y1={mOy + PH - 16} x2={mOx + 34}     y2={mOy + 6}       stroke="#aaa" strokeWidth="1.2" markerEnd="url(#so-blk)"/>

        {/* Asymptote */}
        <path d={mkPath(asymMag, smy)} fill="none" stroke="#888" strokeWidth="1.2" strokeDasharray="8,4"/>

        {allMag.map((data, i) => (
          <path key={i} d={mkPath(data, smy)} fill="none" stroke={zColors[i]} strokeWidth={i < 3 ? 2.2 : 1.8} clipPath="url(#so-m)"/>
        ))}

        {/* ωn line */}
        <line x1={swx(1)} y1={mOy + 6} x2={swx(1)} y2={mOy + PH - 16} stroke="#e07b00" strokeWidth="1.5" strokeDasharray="4,3"/>
        <text x={swx(1)} y={mOy + PH - 4} textAnchor="middle" fontSize={8.5} fill="#e07b00" fontWeight="700">ωₙ=1</text>
        <text x={swx(5)} y={smy(-35)} fontSize={8.5} fill="#888" fontStyle="italic">−40 dB/dec</text>

        {/* Phase */}
        <rect x={pOx} y={pOy} width={PW} height={PH} rx={4} fill="#fff" stroke="#f0cece" strokeWidth="1.5"/>
        <text x={pOx + PW / 2} y={pOy - 8} textAnchor="middle" fontSize={11} fill="#7B0D0D" fontWeight="700">Phase [°]</text>

        {wTicks.map(wv => (
          <line key={wv} x1={swx(wv)} y1={pOy + 6} x2={swx(wv)} y2={pOy + PH - 16} stroke="#f5f5f5" strokeWidth="1"/>
        ))}
        {[0, -45, -90, -135, -180].map(pv => (
          <g key={pv}>
            <line x1={pOx + 34} y1={spy(pv)} x2={pOx + PW - 12} y2={spy(pv)} stroke="#f5f5f5" strokeWidth="1"/>
            <text x={pOx + 30} y={spy(pv) + 3} textAnchor="end" fontSize={8.5} fill="#ccc">{pv}°</text>
          </g>
        ))}
        <line x1={pOx + 34} y1={spy(-90)} x2={pOx + PW - 12} y2={spy(-90)} stroke="#e07b00" strokeWidth="1.2" strokeDasharray="4,3" opacity="0.7"/>
        <text x={swx(1)} y={pOy + PH - 4} textAnchor="middle" fontSize={8.5} fill="#e07b00" fontWeight="700">ωₙ</text>
        <line x1={pOx + 34} y1={pOy + PH - 16} x2={pOx + PW - 8} y2={pOy + PH - 16} stroke="#aaa" strokeWidth="1.2" markerEnd="url(#so-blk)"/>
        <line x1={pOx + 34} y1={pOy + PH - 16} x2={pOx + 34}     y2={pOy + 6}       stroke="#aaa" strokeWidth="1.2" markerEnd="url(#so-blk)"/>

        {allPhs.map((data, i) => (
          <path key={i} d={data.map((v, j) =>
            `${j === 0 ? 'M' : 'L'}${swx(ws[j]).toFixed(1)},${spy(v).toFixed(1)}`).join(' ')}
            fill="none" stroke={zColors[i]} strokeWidth={i < 3 ? 2.2 : 1.8} clipPath="url(#so-p)"/>
        ))}

        {/* Note: all pass through −90° at ωn */}
        <text x={swx(1) + 6} y={spy(-90) - 4} fontSize={8.5} fill="#e07b00">All: −90° at ωₙ</text>
        <text x={pOx + 36} y={pOy + PH - 4} fontSize={8} fill="#aaa">ω [rad/s]</text>

        {/* RIGHT: Resonance table + legend */}
        <rect x={436} y={mOy} width={454} height={350} rx={5} fill="#fff" stroke="#f0cece" strokeWidth="1.5"/>
        <text x={663} y={mOy + 18} textAnchor="middle" fontSize={12} fill="#7B0D0D" fontWeight="700">
          Resonant Peak &amp; Damping Table
        </text>

        {/* Legend */}
        {zetaVals.map((z, i) => (
          <g key={i}>
            <line x1={444} y1={mOy + 32 + i * 22} x2={468} y2={mOy + 32 + i * 22} stroke={zColors[i]} strokeWidth={i < 3 ? 2.2 : 1.8}/>
            <text x={472} y={mOy + 36 + i * 22} fontSize={10} fill={zColors[i]} fontWeight="700">ζ = {z}</text>
            <text x={550} y={mOy + 36 + i * 22} fontSize={9.5} fill="#555">{
              z < Math.SQRT1_2
                ? `Peak: ${(20 * Math.log10(1 / (2 * z * Math.sqrt(1 - z * z)))).toFixed(1)} dB at ωr=${(wn * Math.sqrt(1 - 2 * z * z)).toFixed(3)} r/s`
                : z === Math.SQRT1_2
                  ? 'Maximally flat (no peak, −3 dB at ωₙ)'
                  : 'Overdamped — no resonance peak'
            }</text>
          </g>
        ))}

        {/* Formula boxes */}
        <rect x={444} y={mOy + 190} width={438} height={46} rx={3} fill="#FEF0F0" stroke="#c0392b" strokeWidth="1.2"/>
        <text x={663} y={mOy + 204} textAnchor="middle" fontSize={10} fill="#7B0D0D" fontWeight="700">Resonant frequency ωᵣ (exists for ζ &lt; 1/√2):</text>
        <text x={663} y={mOy + 220} textAnchor="middle" fontSize={10} fill="#333" fontFamily="'Courier New',monospace">ωᵣ = ωₙ√(1−2ζ²)   |   Mr_dB = −20 log₁₀(2ζ√(1−ζ²))</text>
        <text x={663} y={mOy + 232} textAnchor="middle" fontSize={9.5} fill="#555">At ζ=0.707: ωᵣ=0 (peak just disappears), Mr=0 dB</text>

        <rect x={444} y={mOy + 244} width={438} height={46} rx={3} fill="#eaf3ff" stroke="#2E75B6" strokeWidth="1.2"/>
        <text x={663} y={mOy + 258} textAnchor="middle" fontSize={10} fill="#7B0D0D" fontWeight="700">Phase passes through −90° at ω=ωₙ for ALL ζ:</text>
        <text x={663} y={mOy + 274} textAnchor="middle" fontSize={10} fill="#333" fontFamily="'Courier New',monospace">∠G(jωₙ) = −atan2(2ζ·1, 1−1) = −atan2(2ζ, 0) = −90°</text>
        <text x={663} y={mOy + 286} textAnchor="middle" fontSize={9.5} fill="#555">This is universal — independent of damping ratio</text>

        <rect x={444} y={mOy + 298} width={438} height={40} rx={3} fill="#f0fff4" stroke="#27ae60" strokeWidth="1.2"/>
        <text x={663} y={mOy + 312} textAnchor="middle" fontSize={10} fill="#7B0D0D" fontWeight="700">High-frequency asymptote: −40 dB/decade  (two poles)</text>
        <text x={663} y={mOy + 328} textAnchor="middle" fontSize={9.5} fill="#555">Phase ranges from 0° to −180° — double the first-order range</text>
      </svg>
      <div style={S.dCaption}>The second-order system Bode plot shows the −40 dB/decade high-frequency roll-off (twice that of a first-order pole). For ζ &lt; 0.707 a resonant peak appears at ωᵣ=ωₙ√(1−2ζ²), growing dramatically as ζ→0. The remarkable universal property is that all curves pass through exactly −90° phase at ω=ωₙ regardless of damping ratio — this is the basis for reading ωₙ from a measured Bode plot.</div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// Diagram 5 — Building Blocks: Gain, Integrator, Differentiator
// ─────────────────────────────────────────────────────────────────
function DiagramBuildingBlocks() {
  const W = 900, H = 370;

  const wMin = 0.01, wMax = 100, Npts = 200;
  const ws = logSpace(wMin, wMax, Npts);

  // All six basic Bode building blocks
  const blocks = [
    { label: 'K = 10 (gain)', formula: 'G(s)=10',
      mag: ws.map(() => 20),
      phs: ws.map(() => 0),
      color: '#555', dash: 'none' },
    { label: 'Integrator 1/s', formula: 'G(s)=1/s',
      mag: ws.map(w => -20 * Math.log10(w)),
      phs: ws.map(() => -90),
      color: '#c0392b', dash: 'none' },
    { label: 'Differentiator s', formula: 'G(s)=s',
      mag: ws.map(w => 20 * Math.log10(w)),
      phs: ws.map(() => 90),
      color: '#2E75B6', dash: 'none' },
    { label: 'Double integrator 1/s²', formula: 'G(s)=1/s²',
      mag: ws.map(w => -40 * Math.log10(w)),
      phs: ws.map(() => -180),
      color: '#e07b00', dash: '6,3' },
    { label: 'Pole 1/(0.1s+1)', formula: 'G(s)=1/(0.1s+1)',
      mag: ws.map(w => -20 * Math.log10(Math.sqrt(1 + (0.1 * w) ** 2))),
      phs: ws.map(w => -Math.atan(0.1 * w) * 180 / Math.PI),
      color: '#27ae60', dash: 'none' },
    { label: 'Dead time e^{-0.5s}', formula: 'G(s)=e^{−0.5s}',
      mag: ws.map(() => 0),
      phs: ws.map(w => -0.5 * w * 180 / Math.PI),
      color: '#9b59b6', dash: '4,2' },
  ];

  const PW = 380, PH = 130;
  const mOx = 22, mOy = 28, pOx = 22, pOy = 180;
  const swx = w   => mOx + 36 + (Math.log10(w / wMin) / Math.log10(wMax / wMin)) * (PW - 48);
  const smy = dB  => mOy + PH - 18 - ((dB + 80) / 160) * (PH - 28);
  const spy = ph  => pOy + PH - 18 - ((ph + 200) / 310) * (PH - 28);

  const wTicks = [0.01, 0.1, 1, 10, 100];

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Basic Bode Building Blocks — Gain, Integrator, Differentiator, Dead Time</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', display: 'block' }}>
        <defs>
          <Arr id="bb-blk" color="#aaa"/>
          <clipPath id="bb-m"><rect x={mOx + 34} y={mOy + 2} width={PW - 44} height={PH - 18}/></clipPath>
          <clipPath id="bb-p"><rect x={pOx + 34} y={pOy + 2} width={PW - 44} height={PH - 18}/></clipPath>
        </defs>

        {/* Magnitude */}
        <rect x={mOx} y={mOy} width={PW} height={PH} rx={4} fill="#fff" stroke="#f0cece" strokeWidth="1.5"/>
        <text x={mOx + PW / 2} y={mOy - 8} textAnchor="middle" fontSize={11} fill="#7B0D0D" fontWeight="700">Magnitude [dB]</text>
        {wTicks.map(wv => (
          <g key={wv}>
            <line x1={swx(wv)} y1={mOy + 4} x2={swx(wv)} y2={mOy + PH - 16} stroke="#f5f5f5" strokeWidth="1"/>
            <text x={swx(wv)} y={mOy + PH - 4} textAnchor="middle" fontSize={8} fill="#ccc">{wv}</text>
          </g>
        ))}
        {[-80, -40, 0, 40, 80].map(mv => (
          <g key={mv}>
            <line x1={mOx + 34} y1={smy(mv)} x2={mOx + PW - 12} y2={smy(mv)} stroke="#f5f5f5" strokeWidth="1"/>
            <text x={mOx + 30} y={smy(mv) + 3} textAnchor="end" fontSize={8} fill="#ccc">{mv}</text>
          </g>
        ))}
        <line x1={mOx + 34} y1={smy(0)} x2={mOx + PW - 12} y2={smy(0)} stroke="#aaa" strokeWidth="1" strokeDasharray="3,3"/>
        <line x1={mOx + 34} y1={mOy + PH - 16} x2={mOx + PW - 8} y2={mOy + PH - 16} stroke="#aaa" strokeWidth="1.2" markerEnd="url(#bb-blk)"/>
        <line x1={mOx + 34} y1={mOy + PH - 16} x2={mOx + 34}     y2={mOy + 4}       stroke="#aaa" strokeWidth="1.2" markerEnd="url(#bb-blk)"/>

        {blocks.map(({ mag, color, dash }) => (
          <path key={color + dash}
            d={mag.map((v, i) => `${i === 0 ? 'M' : 'L'}${swx(ws[i]).toFixed(1)},${Math.max(mOy + 2, Math.min(mOy + PH - 2, smy(v))).toFixed(1)}`).join(' ')}
            fill="none" stroke={color} strokeWidth="2" strokeDasharray={dash === 'none' ? undefined : dash} clipPath="url(#bb-m)"/>
        ))}

        {/* Phase */}
        <rect x={pOx} y={pOy} width={PW} height={PH} rx={4} fill="#fff" stroke="#f0cece" strokeWidth="1.5"/>
        <text x={pOx + PW / 2} y={pOy - 8} textAnchor="middle" fontSize={11} fill="#7B0D0D" fontWeight="700">Phase [°]</text>
        {wTicks.map(wv => (
          <line key={wv} x1={swx(wv)} y1={pOy + 4} x2={swx(wv)} y2={pOy + PH - 16} stroke="#f5f5f5" strokeWidth="1"/>
        ))}
        {[-200, -180, -90, 0, 90].map(pv => (
          <g key={pv}>
            <line x1={pOx + 34} y1={spy(pv)} x2={pOx + PW - 12} y2={spy(pv)} stroke="#f5f5f5" strokeWidth="1"/>
            <text x={pOx + 30} y={spy(pv) + 3} textAnchor="end" fontSize={8} fill="#ccc">{pv}°</text>
          </g>
        ))}
        <line x1={pOx + 34} y1={pOy + PH - 16} x2={pOx + PW - 8} y2={pOy + PH - 16} stroke="#aaa" strokeWidth="1.2" markerEnd="url(#bb-blk)"/>
        <line x1={pOx + 34} y1={pOy + PH - 16} x2={pOx + 34}     y2={pOy + 4}       stroke="#aaa" strokeWidth="1.2" markerEnd="url(#bb-blk)"/>

        {blocks.map(({ phs, color, dash }) => (
          <path key={color + dash + 'p'}
            d={phs.map((v, i) => `${i === 0 ? 'M' : 'L'}${swx(ws[i]).toFixed(1)},${Math.max(pOy + 2, Math.min(pOy + PH - 2, spy(v))).toFixed(1)}`).join(' ')}
            fill="none" stroke={color} strokeWidth="2" strokeDasharray={dash === 'none' ? undefined : dash} clipPath="url(#bb-p)"/>
        ))}
        <text x={pOx + 36} y={pOy + PH - 4} fontSize={8} fill="#aaa">ω [rad/s]</text>

        {/* RIGHT: Properties table */}
        <rect x={418} y={mOy} width={472} height={330} rx={5} fill="#fff" stroke="#f0cece" strokeWidth="1.5"/>
        <text x={654} y={mOy + 18} textAnchor="middle" fontSize={12} fill="#7B0D0D" fontWeight="700">Building Block Properties</text>

        {/* Table header */}
        <rect x={426} y={mOy + 26} width={456} height={22} rx={2} fill="#c0392b"/>
        {['Element', 'Magnitude', 'Phase', 'Slope'].map((h, i) => (
          <text key={i} x={[434, 536, 648, 740][i]} y={mOy + 41} fontSize={9} fill="#fff" fontWeight="700">{h}</text>
        ))}

        {[
          { el: 'Gain K', mag: '20 log₁₀K', phs: '0°', slope: '0', color: '#555' },
          { el: 'K < 1 (attenuation)', mag: '< 0 dB', phs: '0°', slope: '0', color: '#888' },
          { el: 'Integrator 1/s', mag: '−20 log₁₀ω', phs: '−90°', slope: '−20/dec', color: '#c0392b' },
          { el: 'Double int. 1/s²', mag: '−40 log₁₀ω', phs: '−180°', slope: '−40/dec', color: '#e07b00' },
          { el: 'Differentiator s', mag: '+20 log₁₀ω', phs: '+90°', slope: '+20/dec', color: '#2E75B6' },
          { el: 'Pole 1/(τs+1)', mag: '0→−20/dec', phs: '0°→−90°', slope: '−20/dec', color: '#27ae60' },
          { el: 'Zero (τs+1)', mag: '0→+20/dec', phs: '0°→+90°', slope: '+20/dec', color: '#9b59b6' },
          { el: 'Dead time e^{-Ls}', mag: '0 dB (all ω)', phs: '−L·ω·(180/π)', slope: '0 mag', color: '#9b59b6' },
        ].map(({ el, mag, phs: ph, slope, color }, i) => (
          <g key={i}>
            <rect x={426} y={mOy + 48 + i * 22} width={456} height={22} fill={i % 2 === 0 ? '#fff5f5' : '#fff'}/>
            <text x={434} y={mOy + 63 + i * 22} fontSize={9}   fill={color} fontWeight="700">{el}</text>
            <text x={536} y={mOy + 63 + i * 22} fontSize={9}   fill="#333" fontFamily="'Courier New',monospace">{mag}</text>
            <text x={648} y={mOy + 63 + i * 22} fontSize={9}   fill="#333" fontFamily="'Courier New',monospace">{ph}</text>
            <text x={740} y={mOy + 63 + i * 22} fontSize={9.5} fill={color} fontFamily="'Courier New',monospace">{slope}</text>
          </g>
        ))}

        {/* Key rule */}
        <rect x={426} y={mOy + 228} width={456} height={32} rx={3} fill="#FEF0F0" stroke="#c0392b" strokeWidth="1.2"/>
        <text x={654} y={mOy + 242} textAnchor="middle" fontSize={10} fill="#7B0D0D" fontWeight="700">Composition Rule (log scale makes this work):</text>
        <text x={654} y={mOy + 256} textAnchor="middle" fontSize={9.5} fill="#333" fontFamily="'Courier New',monospace">|G₁G₂| (dB) = |G₁| (dB) + |G₂| (dB)   |   ∠G₁G₂ = ∠G₁ + ∠G₂</text>

        {/* Dead time note */}
        <rect x={426} y={mOy + 268} width={456} height={52} rx={3} fill="#f5d0ff" stroke="#9b59b6" strokeWidth="1.2"/>
        <text x={654} y={mOy + 282} textAnchor="middle" fontSize={10} fill="#7B0D0D" fontWeight="700">{"Dead time e^{-Ls}: magnitude = 0 dB, phase = \u2212L\u00b7\u03c9 rad"}</text>
        <text x={654} y={mOy + 296} textAnchor="middle" fontSize={9.5} fill="#555">Phase lag grows linearly with frequency (unbounded)</text>
        <text x={654} y={mOy + 310} textAnchor="middle" fontSize={9.5} fill="#555">This is why large dead time severely limits achievable bandwidth</text>
      </svg>
      <div style={S.dCaption}>Every transfer function is composed from these six building blocks. The power of the Bode plot is that magnitudes (dB) simply add and phases add too — products in the s-domain become sums in the Bode log scale. Dead time e^(−Ls) is particularly important: zero magnitude contribution but unbounded phase lag proportional to ω, which ultimately limits the bandwidth of any system with delay.</div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// Diagram 6 — Composite Bode Construction (step-by-step)
// ─────────────────────────────────────────────────────────────────
function DiagramCompositeBode() {
  const W = 900, H = 390;

  // G(s) = 10(s+2) / (s(s+0.5)(s+20))
  // = 2/0.5/20 · (s/2+1) / (s · (s/0.5+1)/0.5 · (s/20+1)/20)
  // Written as: G(s) = 10(s+2)/[s(s+0.5)(s+20)]
  // DC gain via type number... integrator → type 1
  // Components: K=10·2/(0.5·20) = 2, integrator 1/s, pole at ω=0.5, zero at ω=2, pole at ω=20

  const wMin = 0.05, wMax = 200, Npts = 250;
  const ws = logSpace(wMin, wMax, Npts);

  // Individual components
  const compGain = ws.map(() => 20 * Math.log10(2));         // K=2 → 6 dB
  const compInt  = ws.map(w => -20 * Math.log10(w));          // 1/s
  const compP1   = ws.map(w => -20 * Math.log10(Math.sqrt(1 + (w / 0.5) ** 2))); // pole at 0.5
  const compZ1   = ws.map(w =>  20 * Math.log10(Math.sqrt(1 + (w / 2)   ** 2)));  // zero at 2
  const compP2   = ws.map(w => -20 * Math.log10(Math.sqrt(1 + (w / 20)  ** 2)));  // pole at 20

  // Total exact
  const totalMag = ws.map(w => {
    const num = 10 * Math.sqrt(1 + (w / 2) ** 2);
    const den = w * Math.sqrt(1 + (w / 0.5) ** 2) * Math.sqrt(1 + (w / 20) ** 2);
    return 20 * Math.log10(num / den);
  });
  const totalPhs = ws.map(w => {
    return (-90 + Math.atan(w / 2) - Math.atan(w / 0.5) - Math.atan(w / 20)) * 180 / Math.PI;
  });

  // Asymptote (piecewise)
  const asymMag = ws.map(w => {
    let m = 20 * Math.log10(2);
    m += -20 * Math.log10(w);    // integrator
    if (w > 0.5) m += -20 * Math.log10(w / 0.5);
    if (w > 2)   m +=  20 * Math.log10(w / 2);
    if (w > 20)  m += -20 * Math.log10(w / 20);
    return m;
  });

  const PW = 400, PH = 145;
  const mOx = 22, mOy = 26, pOx = 22, pOy = 192;
  const swx = w   => mOx + 36 + (Math.log10(w / wMin) / Math.log10(wMax / wMin)) * (PW - 50);
  const smy = dB  => mOy + PH - 20 - ((dB + 80) / 120) * (PH - 30);
  const spy = ph  => pOy + PH - 20 - ((ph + 250) / 250) * (PH - 30);

  const wTicks = [0.05, 0.1, 0.5, 1, 2, 5, 10, 20, 50, 100, 200];

  const mkMag = data => data.map((v, i) =>
    `${i === 0 ? 'M' : 'L'}${swx(ws[i]).toFixed(1)},${Math.max(mOy + 2, Math.min(mOy + PH - 2, smy(v))).toFixed(1)}`).join(' ');
  const mkPhs = data => data.map((v, i) =>
    `${i === 0 ? 'M' : 'L'}${swx(ws[i]).toFixed(1)},${Math.max(pOy + 2, Math.min(pOy + PH - 2, spy(v))).toFixed(1)}`).join(' ');

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Composite Bode Construction — G(s) = 10(s+2) / [s(s+0.5)(s+20)]</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', display: 'block' }}>
        <defs>
          <Arr id="cb-blk" color="#aaa"/>
          <clipPath id="cb-m"><rect x={mOx + 34} y={mOy + 2} width={PW - 46} height={PH - 18}/></clipPath>
          <clipPath id="cb-p"><rect x={pOx + 34} y={pOy + 2} width={PW - 46} height={PH - 18}/></clipPath>
        </defs>

        {/* Magnitude plot */}
        <rect x={mOx} y={mOy} width={PW} height={PH} rx={4} fill="#fff" stroke="#f0cece" strokeWidth="1.5"/>
        <text x={mOx + PW / 2} y={mOy - 8} textAnchor="middle" fontSize={11} fill="#7B0D0D" fontWeight="700">
          Magnitude — Component Contributions (sum in dB)
        </text>
        {wTicks.map(wv => (
          <g key={wv}>
            <line x1={swx(wv)} y1={mOy + 4} x2={swx(wv)} y2={mOy + PH - 18} stroke="#f5f5f5" strokeWidth="1"/>
            <text x={swx(wv)} y={mOy + PH - 6} textAnchor="middle" fontSize={7.5} fill="#ccc">{wv}</text>
          </g>
        ))}
        {[-80, -40, 0, 40].map(mv => (
          <g key={mv}>
            <line x1={mOx + 34} y1={smy(mv)} x2={mOx + PW - 12} y2={smy(mv)} stroke="#f5f5f5" strokeWidth="1"/>
            <text x={mOx + 30} y={smy(mv) + 3} textAnchor="end" fontSize={8} fill="#ccc">{mv}</text>
          </g>
        ))}
        <line x1={mOx + 34} y1={smy(0)} x2={mOx + PW - 12} y2={smy(0)} stroke="#aaa" strokeWidth="1" strokeDasharray="4,3"/>
        <line x1={mOx + 34} y1={mOy + PH - 18} x2={mOx + PW - 8} y2={mOy + PH - 18} stroke="#aaa" strokeWidth="1.2" markerEnd="url(#cb-blk)"/>
        <line x1={mOx + 34} y1={mOy + PH - 18} x2={mOx + 34}     y2={mOy + 4}       stroke="#aaa" strokeWidth="1.2" markerEnd="url(#cb-blk)"/>

        {/* Individual components */}
        {[
          { data: compGain, color: '#555',    dash: '5,3', label: 'K=2 (+6dB)' },
          { data: compInt,  color: '#c0392b', dash: '5,3', label: '1/s' },
          { data: compP1,   color: '#e07b00', dash: '5,3', label: 'pole ω=0.5' },
          { data: compZ1,   color: '#27ae60', dash: '5,3', label: 'zero ω=2' },
          { data: compP2,   color: '#9b59b6', dash: '5,3', label: 'pole ω=20' },
        ].map(({ data, color, dash }) => (
          <path key={color} d={mkMag(data)} fill="none" stroke={color} strokeWidth="1.2"
            strokeDasharray={dash} clipPath="url(#cb-m)" opacity="0.7"/>
        ))}

        {/* Asymptote */}
        <path d={mkMag(asymMag)} fill="none" stroke="#aaa" strokeWidth="1.5" strokeDasharray="8,4" clipPath="url(#cb-m)"/>
        {/* Total exact */}
        <path d={mkMag(totalMag)} fill="none" stroke="#c0392b" strokeWidth="2.8" clipPath="url(#cb-m)"/>

        {/* Corner frequency markers */}
        {[0.5, 2, 20].map((wc, i) => {
          const labels = ['pole ω=0.5', 'zero ω=2', 'pole ω=20'];
          const colors = ['#e07b00', '#27ae60', '#9b59b6'];
          return (
            <g key={wc}>
              <line x1={swx(wc)} y1={mOy + 4} x2={swx(wc)} y2={mOy + PH - 18} stroke={colors[i]} strokeWidth="1.2" strokeDasharray="3,2" opacity="0.7"/>
              <text x={swx(wc)} y={mOy + PH - 8} textAnchor="middle" fontSize={7.5} fill={colors[i]} fontWeight="700">{labels[i]}</text>
            </g>
          );
        })}

        {/* Phase plot */}
        <rect x={pOx} y={pOy} width={PW} height={PH} rx={4} fill="#fff" stroke="#f0cece" strokeWidth="1.5"/>
        <text x={pOx + PW / 2} y={pOy - 8} textAnchor="middle" fontSize={11} fill="#7B0D0D" fontWeight="700">Phase [°]</text>
        {wTicks.map(wv => (
          <line key={wv} x1={swx(wv)} y1={pOy + 4} x2={swx(wv)} y2={pOy + PH - 18} stroke="#f5f5f5" strokeWidth="1"/>
        ))}
        {[-250, -180, -90, 0].map(pv => (
          <g key={pv}>
            <line x1={pOx + 34} y1={spy(pv)} x2={pOx + PW - 12} y2={spy(pv)} stroke="#f5f5f5" strokeWidth="1"/>
            <text x={pOx + 30} y={spy(pv) + 3} textAnchor="end" fontSize={8} fill="#ccc">{pv}°</text>
          </g>
        ))}
        <line x1={pOx + 34} y1={spy(-180)} x2={pOx + PW - 12} y2={spy(-180)} stroke="#c0392b" strokeWidth="1" strokeDasharray="5,3" opacity="0.5"/>
        <line x1={pOx + 34} y1={pOy + PH - 18} x2={pOx + PW - 8} y2={pOy + PH - 18} stroke="#aaa" strokeWidth="1.2" markerEnd="url(#cb-blk)"/>
        <line x1={pOx + 34} y1={pOy + PH - 18} x2={pOx + 34}     y2={pOy + 4}       stroke="#aaa" strokeWidth="1.2" markerEnd="url(#cb-blk)"/>
        <path d={mkPhs(totalPhs)} fill="none" stroke="#2E75B6" strokeWidth="2.8" clipPath="url(#cb-p)"/>
        <text x={pOx + 36} y={pOy + PH - 6} fontSize={8} fill="#aaa">ω</text>

        {/* Right: step-by-step construction */}
        <rect x={436} y={mOy} width={454} height={360} rx={5} fill="#fff" stroke="#f0cece" strokeWidth="1.5"/>
        <text x={663} y={mOy + 18} textAnchor="middle" fontSize={12} fill="#7B0D0D" fontWeight="700">Step-by-Step Construction</text>

        <text x={444} y={mOy + 34} fontSize={10.5} fill="#7B0D0D" fontWeight="700">G(s) = 10(s+2) / [s(s+0.5)(s+20)]  =  2·(s/2+1) / [s·(s/0.5+1)·(s/20+1)]</text>

        {[
          { step: '① DC/low-ω gain', color: '#555',
            lines: ['K = 10·2/(1·0.5·20) = 2  →  20 log₁₀(2) = +6.02 dB',
                    'Draw horizontal line at +6 dB for ω → 0'] },
          { step: '② Add integrator 1/s', color: '#c0392b',
            lines: ['Passes through ω=1 at value=0 dB  (|1/jω|=1 at ω=1)',
                    'Add −20 dB/decade slope starting from ω=0'] },
          { step: '③ Pole at ω=0.5', color: '#e07b00',
            lines: ['Breaks slope by additional −20 dB/dec at ω=0.5',
                    'Total slope above ω=0.5: −20+(−20) = −40 dB/dec'] },
          { step: '④ Zero at ω=2', color: '#27ae60',
            lines: ['Adds +20 dB/dec at ω=2',
                    'Total slope above ω=2: −40+20 = −20 dB/dec'] },
          { step: '⑤ Pole at ω=20', color: '#9b59b6',
            lines: ['Adds −20 dB/dec at ω=20',
                    'Total slope above ω=20: −20+(−20) = −40 dB/dec'] },
          { step: '⑥ Sum of phases', color: '#2E75B6',
            lines: ['−90° (integrator) + phase(pole 0.5) + phase(zero 2) + phase(pole 20)',
                    'Draw each contribution; add at each frequency'] },
        ].map(({ step, color, lines }, i) => (
          <g key={i}>
            <rect x={444} y={mOy + 48 + i * 50} width={438} height={46} rx={3}
              fill={i % 2 === 0 ? '#fafafa' : '#fff'} stroke={color} strokeWidth="0.8"/>
            <text x={452} y={mOy + 62 + i * 50} fontSize={10} fill={color} fontWeight="700">{step}</text>
            {lines.map((l, j) => (
              <text key={j} x={452} y={mOy + 76 + i * 50 + j * 14} fontSize={9} fill="#333">{l}</text>
            ))}
          </g>
        ))}

        {/* Final slope summary */}
        <rect x={444} y={mOy + 352} width={438} height={0} rx={2}/>
      </svg>
      <div style={S.dCaption}>Constructing the composite Bode plot for G(s)=10(s+2)/[s(s+0.5)(s+20)] step-by-step: start with the DC gain, add the integrator's −20 dB/decade slope, then break the slope at each corner frequency — down at poles (ω=0.5 and ω=20), up at zeros (ω=2). The dashed coloured lines are the individual components; summing them gives the asymptote (grey dashes); the red/blue curves are the exact magnitude and phase.</div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// Diagram 7 — Gain Margin, Phase Margin, and Stability
// ─────────────────────────────────────────────────────────────────
function DiagramStabilityMargins() {
  const W = 900, H = 390;

  // Open-loop G(s)C(s) = K / (s(s/2+1)(s/8+1))
  // Gain crossover: |GC(jω)| = 1
  // Phase crossover: ∠GC(jω) = −180°
  const K_nominal = 2.0;

  const wMin = 0.05, wMax = 50, Npts = 250;
  const ws = logSpace(wMin, wMax, Npts);

  const gMag = (K) => ws.map(w => {
    const m = K / (w * Math.sqrt(1 + (w / 2) ** 2) * Math.sqrt(1 + (w / 8) ** 2));
    return 20 * Math.log10(m);
  });
  const gPhs = ws.map(w =>
    (-90 - Math.atan(w / 2) - Math.atan(w / 8)) * 180 / Math.PI
  );

  // Find gain crossover (0 dB) for nominal
  const magNom = gMag(K_nominal);
  let gcIdx = 0;
  for (let i = 1; i < magNom.length; i++) {
    if (magNom[i - 1] > 0 && magNom[i] <= 0) { gcIdx = i; break; }
  }
  const wgc = ws[gcIdx];
  const phsAtGC = gPhs[gcIdx];
  const PM = 180 + phsAtGC;

  // Find phase crossover (−180°)
  let pcIdx = 0;
  for (let i = 1; i < gPhs.length; i++) {
    if (gPhs[i - 1] > -180 && gPhs[i] <= -180) { pcIdx = i; break; }
  }
  const wpc = ws[pcIdx] || 30;
  const magAtPC = magNom[pcIdx] || -20;
  const GM_dB = -magAtPC;

  const PW = 390, PH = 145;
  const mOx = 20, mOy = 26, pOx = 20, pOy = 195;
  const swx = w   => mOx + 36 + (Math.log10(w / wMin) / Math.log10(wMax / wMin)) * (PW - 50);
  const smy = dB  => mOy + PH - 20 - ((dB + 60) / 100) * (PH - 30);
  const spy = ph  => pOy + PH - 20 - ((ph + 200) / 200) * (PH - 30);

  const mkPath = (data, sy) => data.map((v, i) =>
    `${i === 0 ? 'M' : 'L'}${swx(ws[i]).toFixed(1)},${Math.max(mOy + 2, Math.min(mOy + PH, sy(v))).toFixed(1)}`).join(' ');

  const wTicks = [0.1, 0.2, 0.5, 1, 2, 5, 10, 20, 50];

  // Also compute for K_higher (shows reduced margins)
  const magHigh = gMag(8.0);
  let gcIdxH = 0;
  for (let i = 1; i < magHigh.length; i++) {
    if (magHigh[i - 1] > 0 && magHigh[i] <= 0) { gcIdxH = i; break; }
  }
  const PM_high = 180 + gPhs[gcIdxH];

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Gain Margin, Phase Margin &amp; Closed-Loop Stability from the Bode Plot</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', display: 'block' }}>
        <defs>
          <Arr id="sm-blk" color="#aaa"/>
          <Arr id="sm-red" color="#c0392b"/>
          <Arr id="sm-grn" color="#27ae60"/>
          <Arr id="sm-orn" color="#e07b00"/>
          <clipPath id="sm-m"><rect x={mOx + 34} y={mOy + 2} width={PW - 46} height={PH - 18}/></clipPath>
          <clipPath id="sm-p"><rect x={pOx + 34} y={pOy + 2} width={PW - 46} height={PH - 18}/></clipPath>
        </defs>

        {/* Magnitude */}
        <rect x={mOx} y={mOy} width={PW} height={PH} rx={4} fill="#fff" stroke="#f0cece" strokeWidth="1.5"/>
        <text x={mOx + PW / 2} y={mOy - 8} textAnchor="middle" fontSize={11} fill="#7B0D0D" fontWeight="700">
          Open-Loop Magnitude — G(s)C(s) = K/[s(s/2+1)(s/8+1)]
        </text>
        {wTicks.map(wv => (
          <g key={wv}>
            <line x1={swx(wv)} y1={mOy + 4} x2={swx(wv)} y2={mOy + PH - 18} stroke="#f5f5f5" strokeWidth="1"/>
            <text x={swx(wv)} y={mOy + PH - 6} textAnchor="middle" fontSize={8} fill="#ccc">{wv}</text>
          </g>
        ))}
        {[-60, -40, -20, 0, 20, 40].map(mv => (
          <g key={mv}>
            <line x1={mOx + 34} y1={smy(mv)} x2={mOx + PW - 12} y2={smy(mv)} stroke="#f5f5f5" strokeWidth="1"/>
            <text x={mOx + 30} y={smy(mv) + 3} textAnchor="end" fontSize={8} fill="#ccc">{mv}</text>
          </g>
        ))}
        <line x1={mOx + 34} y1={smy(0)} x2={mOx + PW - 12} y2={smy(0)} stroke="#aaa" strokeWidth="1.5" strokeDasharray="5,4"/>
        <text x={mOx + PW - 8} y={smy(0) - 4} fontSize={8} fill="#aaa">0 dB</text>
        <line x1={mOx + 34} y1={mOy + PH - 18} x2={mOx + PW - 8} y2={mOy + PH - 18} stroke="#aaa" strokeWidth="1.2" markerEnd="url(#sm-blk)"/>
        <line x1={mOx + 34} y1={mOy + PH - 18} x2={mOx + 34}     y2={mOy + 4}       stroke="#aaa" strokeWidth="1.2" markerEnd="url(#sm-blk)"/>

        {/* Nominal and high-gain curves */}
        <path d={mkPath(magHigh, smy)} fill="none" stroke="#e07b00" strokeWidth="1.8" strokeDasharray="6,3" clipPath="url(#sm-m)"/>
        <path d={mkPath(magNom,  smy)} fill="none" stroke="#c0392b" strokeWidth="2.5" clipPath="url(#sm-m)"/>

        {/* Gain crossover annotation */}
        <line x1={swx(wgc)} y1={mOy + 4} x2={swx(wgc)} y2={mOy + PH - 18} stroke="#27ae60" strokeWidth="1.5" strokeDasharray="4,3"/>
        <circle cx={swx(wgc)} cy={smy(0)} r={5} fill="#27ae60" stroke="#fff" strokeWidth="1.5"/>
        <text x={swx(wgc) + 6} y={smy(0) - 6} fontSize={9} fill="#27ae60" fontWeight="700">ωgc={wgc.toFixed(2)}</text>

        {/* Gain margin annotation at phase crossover */}
        {pcIdx > 0 && (
          <>
            <line x1={swx(wpc)} y1={mOy + 4} x2={swx(wpc)} y2={mOy + PH - 18} stroke="#2E75B6" strokeWidth="1.5" strokeDasharray="4,3"/>
            <circle cx={swx(wpc)} cy={smy(magAtPC)} r={5} fill="#2E75B6" stroke="#fff" strokeWidth="1.5"/>
            <line x1={swx(wpc)} y1={smy(0)} x2={swx(wpc)} y2={smy(magAtPC)}
              stroke="#2E75B6" strokeWidth="2" markerStart="url(#sm-grn)" markerEnd="url(#sm-grn)"/>
            <text x={swx(wpc) + 6} y={(smy(0) + smy(magAtPC)) / 2 + 4} fontSize={9} fill="#2E75B6" fontWeight="700">
              GM={GM_dB.toFixed(1)}dB
            </text>
          </>
        )}

        {/* Phase plot */}
        <rect x={pOx} y={pOy} width={PW} height={PH} rx={4} fill="#fff" stroke="#f0cece" strokeWidth="1.5"/>
        <text x={pOx + PW / 2} y={pOy - 8} textAnchor="middle" fontSize={11} fill="#7B0D0D" fontWeight="700">Phase [°]</text>
        {wTicks.map(wv => (
          <line key={wv} x1={swx(wv)} y1={pOy + 4} x2={swx(wv)} y2={pOy + PH - 18} stroke="#f5f5f5" strokeWidth="1"/>
        ))}
        {[-200, -180, -90, 0].map(pv => (
          <g key={pv}>
            <line x1={pOx + 34} y1={spy(pv)} x2={pOx + PW - 12} y2={spy(pv)} stroke="#f5f5f5" strokeWidth="1"/>
            <text x={pOx + 30} y={spy(pv) + 3} textAnchor="end" fontSize={8} fill="#ccc">{pv}°</text>
          </g>
        ))}
        <line x1={pOx + 34} y1={spy(-180)} x2={pOx + PW - 12} y2={spy(-180)} stroke="#c0392b" strokeWidth="1.5" strokeDasharray="5,3" opacity="0.7"/>
        <text x={pOx + 38} y={spy(-180) - 4} fontSize={8.5} fill="#c0392b">−180° line</text>
        <line x1={pOx + 34} y1={pOy + PH - 18} x2={pOx + PW - 8} y2={pOy + PH - 18} stroke="#aaa" strokeWidth="1.2" markerEnd="url(#sm-blk)"/>
        <line x1={pOx + 34} y1={pOy + PH - 18} x2={pOx + 34}     y2={pOy + 4}       stroke="#aaa" strokeWidth="1.2" markerEnd="url(#sm-blk)"/>

        <path d={gPhs.map((v, i) => `${i === 0 ? 'M' : 'L'}${swx(ws[i]).toFixed(1)},${spy(v).toFixed(1)}`).join(' ')}
          fill="none" stroke="#2E75B6" strokeWidth="2.5" clipPath="url(#sm-p)"/>

        {/* Phase margin annotation */}
        <line x1={swx(wgc)} y1={pOy + 4} x2={swx(wgc)} y2={pOy + PH - 18} stroke="#27ae60" strokeWidth="1.5" strokeDasharray="4,3"/>
        <circle cx={swx(wgc)} cy={spy(phsAtGC)} r={5} fill="#27ae60" stroke="#fff" strokeWidth="1.5"/>
        <line x1={swx(wgc)} y1={spy(-180)} x2={swx(wgc)} y2={spy(phsAtGC)}
          stroke="#27ae60" strokeWidth="2" markerStart="url(#sm-grn)" markerEnd="url(#sm-grn)"/>
        <text x={swx(wgc) + 6} y={(spy(-180) + spy(phsAtGC)) / 2 + 4} fontSize={9} fill="#27ae60" fontWeight="700">
          PM={PM.toFixed(1)}°
        </text>
        {/* Phase crossover line */}
        {pcIdx > 0 && (
          <line x1={swx(wpc)} y1={pOy + 4} x2={swx(wpc)} y2={pOy + PH - 18} stroke="#2E75B6" strokeWidth="1.5" strokeDasharray="4,3"/>
        )}
        <text x={pOx + 36} y={pOy + PH - 5} fontSize={8} fill="#aaa">ω [rad/s]</text>

        {/* RIGHT: Stability margins panel */}
        <rect x={428} y={mOy} width={462} height={360} rx={5} fill="#fff" stroke="#f0cece" strokeWidth="1.5"/>
        <text x={659} y={mOy + 18} textAnchor="middle" fontSize={12} fill="#7B0D0D" fontWeight="700">Stability Margins — Definitions &amp; Targets</text>

        {[
          { y: mOy + 28, fill: '#f0fff4', bc: '#27ae60',
            title: 'Phase Margin PM',
            lines: [`PM = 180° + ∠GC(jωgc)  at the gain crossover`,
                    `ωgc = frequency where |GC(jω)| = 1 (0 dB)`,
                    `This system: PM = ${PM.toFixed(1)}°  at ωgc = ${wgc.toFixed(2)} rad/s`,
                    `Meaning: loop can tolerate ${PM.toFixed(1)}° more phase lag before instability`,
                    `Target: PM ≥ 45° (comfortable), ≥ 60° (conservative)`] },
          { y: mOy + 134, fill: '#eaf3ff', bc: '#2E75B6',
            title: 'Gain Margin GM',
            lines: ['GM = −20·log₁₀|GC(jωpc)|  at phase crossover',
                    'ωpc = frequency where ∠GC(jω) = −180°',
                    `This system: GM = ${GM_dB.toFixed(1)} dB  at ωpc ≈ ${wpc.toFixed(2)} rad/s`,
                    `Gain can increase by factor 10^(${(GM_dB / 20).toFixed(2)}) before instability`,
                    'Target: GM ≥ 6 dB (comfortable), ≥ 12 dB (conservative)'] },
          { y: mOy + 240, fill: '#FEF0F0', bc: '#c0392b',
            title: 'Stability Criterion (Bode)',
            lines: ['For minimum-phase systems (no RHP zeros/poles):',
                    'Stable ↔ PM > 0° AND GM > 0 dB',
                    'If PM = 0°: sustained oscillation at ωgc',
                    'If PM < 0° or GM < 0 dB: UNSTABLE',
                    'Both margins must be checked independently'] },
          { y: mOy + 332, fill: '#fff5e6', bc: '#e07b00',
            title: 'Effect of gain change (K)',
            lines: [`K↑: magnitude plot shifts up → ωgc increases → PM decreases`,
                    `K↓: magnitude plot shifts down → ωgc decreases → PM increases`,
                    `K=8 (orange dashes): reduced PM (faster, less stable)`] },
        ].map(({ y, fill, bc, title, lines }) => (
          <g key={y}>
            <rect x={436} y={y} width={446} height={lines.length * 17 + 20} rx={3} fill={fill} stroke={bc} strokeWidth="1"/>
            <text x={444} y={y + 14} fontSize={10} fill="#7B0D0D" fontWeight="700">{title}</text>
            {lines.map((l, j) => (
              <text key={j} x={444} y={y + 28 + j * 16} fontSize={9} fill="#333">▸ {l}</text>
            ))}
          </g>
        ))}
      </svg>
      <div style={S.dCaption}>Phase margin (PM) is read at the gain crossover ωgc: how far the phase is above −180°. Gain margin (GM) is read at the phase crossover ωpc: how many dB the gain can increase before it reaches 0 dB at the unstable frequency. For this three-pole system with K=2, PM≈{PM.toFixed(0)}° and GM≈{GM_dB.toFixed(0)} dB — both healthy. Increasing K shifts the magnitude curve up, moving ωgc right where the phase is more negative, reducing PM.</div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// Diagram 8 — Lead/Lag Compensator Design using Bode
// ─────────────────────────────────────────────────────────────────
function DiagramCompensatorDesign() {
  const W = 900, H = 400;

  const wMin = 0.05, wMax = 100, Npts = 250;
  const ws = logSpace(wMin, wMax, Npts);

  // Uncompensated plant: G(s) = 4 / (s(s/5+1))
  const plantMag = ws.map(w => 20 * Math.log10(4 / (w * Math.sqrt(1 + (w / 5) ** 2))));
  const plantPhs = ws.map(w => (-90 - Math.atan(w / 5)) * 180 / Math.PI);

  // Lead compensator: C_lead(s) = (s/1+1)/(s/8+1)   zero at 1, pole at 8
  // Designed to add phase at ~2.8 rad/s
  const leadMag = ws.map(w => 20 * Math.log10(Math.sqrt(1 + w * w) / Math.sqrt(1 + (w / 8) ** 2)));
  const leadPhs = ws.map(w => (Math.atan(w) - Math.atan(w / 8)) * 180 / Math.PI);

  // Compensated = plant × lead
  const compMag = ws.map((_, i) => plantMag[i] + leadMag[i]);
  const compPhs = ws.map((_, i) => plantPhs[i] + leadPhs[i]);

  // Find crossover frequencies
  let gcPlant = 0, gcComp = 0;
  let gcIdxP = 0, gcIdxC = 0;
  for (let i = 1; i < ws.length; i++) {
    if (plantMag[i - 1] > 0 && plantMag[i] <= 0) { gcIdxP = i; gcPlant = ws[i]; }
    if (compMag[i - 1]  > 0 && compMag[i]  <= 0) { gcIdxC = i; gcComp  = ws[i]; }
  }
  const PM_plant = 180 + plantPhs[gcIdxP];
  const PM_comp  = 180 + compPhs[gcIdxC];

  const PW = 390, PH = 140;
  const mOx = 22, mOy = 26, pOx = 22, pOy = 188;
  const swx = w   => mOx + 36 + (Math.log10(w / wMin) / Math.log10(wMax / wMin)) * (PW - 50);
  const smy = dB  => mOy + PH - 18 - ((dB + 60) / 100) * (PH - 28);
  const spy = ph  => pOy + PH - 18 - ((ph + 200) / 200) * (PH - 28);

  const mkPath = (data, sy) => data.map((v, i) =>
    `${i === 0 ? 'M' : 'L'}${swx(ws[i]).toFixed(1)},${Math.max(mOy + 2, Math.min(mOy + PH, sy(v))).toFixed(1)}`).join(' ');

  const wTicks = [0.1, 0.5, 1, 2, 5, 10, 20, 50, 100];

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Lead Compensator Design via Bode — Increasing Phase Margin</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', display: 'block' }}>
        <defs>
          <Arr id="cd-blk" color="#aaa"/>
          <Arr id="cd-grn" color="#27ae60"/>
          <Arr id="cd-red" color="#c0392b"/>
          <clipPath id="cd-m"><rect x={mOx + 34} y={mOy + 2} width={PW - 46} height={PH - 18}/></clipPath>
          <clipPath id="cd-p"><rect x={pOx + 34} y={pOy + 2} width={PW - 46} height={PH - 18}/></clipPath>
        </defs>

        {/* Magnitude */}
        <rect x={mOx} y={mOy} width={PW} height={PH} rx={4} fill="#fff" stroke="#f0cece" strokeWidth="1.5"/>
        <text x={mOx + PW / 2} y={mOy - 8} textAnchor="middle" fontSize={11} fill="#7B0D0D" fontWeight="700">Loop Magnitude — Plant &amp; Lead Compensator</text>
        {wTicks.map(wv => (
          <g key={wv}>
            <line x1={swx(wv)} y1={mOy + 4} x2={swx(wv)} y2={mOy + PH - 16} stroke="#f5f5f5" strokeWidth="1"/>
            <text x={swx(wv)} y={mOy + PH - 4} textAnchor="middle" fontSize={8} fill="#ccc">{wv}</text>
          </g>
        ))}
        {[-60, -40, -20, 0, 20, 40].map(mv => (
          <g key={mv}>
            <line x1={mOx + 34} y1={smy(mv)} x2={mOx + PW - 12} y2={smy(mv)} stroke="#f5f5f5" strokeWidth="1"/>
            <text x={mOx + 30} y={smy(mv) + 3} textAnchor="end" fontSize={8} fill="#ccc">{mv}</text>
          </g>
        ))}
        <line x1={mOx + 34} y1={smy(0)} x2={mOx + PW - 12} y2={smy(0)} stroke="#aaa" strokeWidth="1" strokeDasharray="5,4"/>
        <line x1={mOx + 34} y1={mOy + PH - 16} x2={mOx + PW - 8} y2={mOy + PH - 16} stroke="#aaa" strokeWidth="1.2" markerEnd="url(#cd-blk)"/>
        <line x1={mOx + 34} y1={mOy + PH - 16} x2={mOx + 34}     y2={mOy + 4}       stroke="#aaa" strokeWidth="1.2" markerEnd="url(#cd-blk)"/>

        {/* Lead compensator magnitude (dashed) */}
        <path d={mkPath(leadMag, smy)} fill="none" stroke="#e07b00" strokeWidth="1.5" strokeDasharray="6,3" clipPath="url(#cd-m)"/>
        {/* Plant magnitude */}
        <path d={mkPath(plantMag, smy)} fill="none" stroke="#c0392b" strokeWidth="2" clipPath="url(#cd-m)"/>
        {/* Compensated */}
        <path d={mkPath(compMag, smy)} fill="none" stroke="#27ae60" strokeWidth="2.5" clipPath="url(#cd-m)"/>

        {/* Crossover annotations */}
        <line x1={swx(gcPlant)} y1={mOy + 4} x2={swx(gcPlant)} y2={mOy + PH - 16} stroke="#c0392b" strokeWidth="1.2" strokeDasharray="3,2"/>
        <circle cx={swx(gcPlant)} cy={smy(0)} r={4} fill="#c0392b" stroke="#fff" strokeWidth="1.5"/>
        <text x={swx(gcPlant)} y={mOy + PH - 4} textAnchor="middle" fontSize={8} fill="#c0392b">ωgc={gcPlant.toFixed(1)}</text>

        <line x1={swx(gcComp)} y1={mOy + 4} x2={swx(gcComp)} y2={mOy + PH - 16} stroke="#27ae60" strokeWidth="1.2" strokeDasharray="3,2"/>
        <circle cx={swx(gcComp)} cy={smy(0)} r={4} fill="#27ae60" stroke="#fff" strokeWidth="1.5"/>
        <text x={swx(gcComp)} y={mOy + PH - 4} textAnchor="middle" fontSize={8} fill="#27ae60">ωgc={gcComp.toFixed(1)}</text>

        {/* Phase */}
        <rect x={pOx} y={pOy} width={PW} height={PH} rx={4} fill="#fff" stroke="#f0cece" strokeWidth="1.5"/>
        <text x={pOx + PW / 2} y={pOy - 8} textAnchor="middle" fontSize={11} fill="#7B0D0D" fontWeight="700">Phase — Phase Margin Improvement</text>
        {wTicks.map(wv => (
          <line key={wv} x1={swx(wv)} y1={pOy + 4} x2={swx(wv)} y2={pOy + PH - 16} stroke="#f5f5f5" strokeWidth="1"/>
        ))}
        {[-200, -180, -90, 0].map(pv => (
          <g key={pv}>
            <line x1={pOx + 34} y1={spy(pv)} x2={pOx + PW - 12} y2={spy(pv)} stroke="#f5f5f5" strokeWidth="1"/>
            <text x={pOx + 30} y={spy(pv) + 3} textAnchor="end" fontSize={8} fill="#ccc">{pv}°</text>
          </g>
        ))}
        <line x1={pOx + 34} y1={spy(-180)} x2={pOx + PW - 12} y2={spy(-180)} stroke="#aaa" strokeWidth="1" strokeDasharray="5,3"/>
        <line x1={pOx + 34} y1={pOy + PH - 16} x2={pOx + PW - 8} y2={pOy + PH - 16} stroke="#aaa" strokeWidth="1.2" markerEnd="url(#cd-blk)"/>
        <line x1={pOx + 34} y1={pOy + PH - 16} x2={pOx + 34}     y2={pOy + 4}       stroke="#aaa" strokeWidth="1.2" markerEnd="url(#cd-blk)"/>

        {/* Lead phase contribution */}
        <path d={leadPhs.map((v, i) => `${i === 0 ? 'M' : 'L'}${swx(ws[i]).toFixed(1)},${spy(v).toFixed(1)}`).join(' ')}
          fill="none" stroke="#e07b00" strokeWidth="1.5" strokeDasharray="6,3" clipPath="url(#cd-p)"/>
        <path d={plantPhs.map((v, i) => `${i === 0 ? 'M' : 'L'}${swx(ws[i]).toFixed(1)},${spy(v).toFixed(1)}`).join(' ')}
          fill="none" stroke="#c0392b" strokeWidth="2" clipPath="url(#cd-p)"/>
        <path d={compPhs.map((v, i) => `${i === 0 ? 'M' : 'L'}${swx(ws[i]).toFixed(1)},${spy(v).toFixed(1)}`).join(' ')}
          fill="none" stroke="#27ae60" strokeWidth="2.5" clipPath="url(#cd-p)"/>

        {/* PM arrows */}
        {[{ gcIdx: gcIdxP, color: '#c0392b', PM: PM_plant, sign: 0 },
          { gcIdx: gcIdxC, color: '#27ae60', PM: PM_comp,  sign: 10 }].map(({ gcIdx, color, PM, sign }) => {
          const x = swx(ws[gcIdx]) + sign;
          return (
            <g key={color}>
              <line x1={x} y1={spy(-180)} x2={x} y2={spy(-180 + PM)}
                stroke={color} strokeWidth="2" markerStart="url(#cd-grn)" markerEnd="url(#cd-grn)"/>
              <text x={x + 5} y={(spy(-180) + spy(-180 + PM)) / 2 + 4} fontSize={9} fill={color} fontWeight="700">
                PM={PM.toFixed(1)}°
              </text>
            </g>
          );
        })}

        <text x={pOx + 36} y={pOy + PH - 5} fontSize={8} fill="#aaa">ω [rad/s]</text>

        {/* Right: design procedure */}
        <rect x={430} y={mOy} width={460} height={370} rx={5} fill="#fff" stroke="#f0cece" strokeWidth="1.5"/>
        <text x={660} y={mOy + 18} textAnchor="middle" fontSize={12} fill="#7B0D0D" fontWeight="700">Lead Compensator Design Steps</text>

        {/* Compensator formula */}
        <rect x={438} y={mOy + 26} width={444} height={44} rx={3} fill="#eaf3ff" stroke="#2E75B6" strokeWidth="1.5"/>
        <text x={660} y={mOy + 42} textAnchor="middle" fontSize={11} fill="#2E75B6" fontFamily="'Courier New',monospace" fontWeight="700">
          C_lead(s) = (s/ωz+1)/(s/ωp+1),  ωp &gt; ωz
        </text>
        <text x={660} y={mOy + 61} textAnchor="middle" fontSize={9.5} fill="#555">
          Max phase lead: φmax = arcsin((N−1)/(N+1))  where N = ωp/ωz
        </text>

        {[
          { n: '①', title: 'Determine required PM increase',
            body: `Current PM = ${PM_plant.toFixed(1)}°. Target: 45° → need +${(45-PM_plant).toFixed(1)}°\nAllow 5–10° safety margin → design for φmax ≈ ${(45-PM_plant+8).toFixed(0)}°` },
          { n: '②', title: 'Compute ratio N from φmax',
            body: `N = (1+sinφmax)/(1−sinφmax)\nFor φmax=${(45-PM_plant+8).toFixed(0)}°: N ≈ ${((1+Math.sin((45-PM_plant+8)*Math.PI/180))/(1-Math.sin((45-PM_plant+8)*Math.PI/180))).toFixed(1)}` },
          { n: '③', title: 'Choose new gain crossover ωgc_new',
            body: `Find ω where |G(jω)| = −10 log₁₀(N) dB (≈ midpoint lift)\nThis places max phase at the desired crossover` },
          { n: '④', title: 'Compute zero and pole locations',
            body: `ωz = ωgc_new / √N  |  ωp = ωgc_new · √N\nThis system: ωz ≈ 1, ωp ≈ 8  →  C = (s+1)/(s/8+1)` },
          { n: '⑤', title: 'Verify compensated margins',
            body: `Compensated PM = ${PM_comp.toFixed(1)}° (target ≥ 45°) ✓\nNew ωgc = ${gcComp.toFixed(1)} rad/s (higher bandwidth) ✓` },
        ].map(({ n, title, body }, i) => (
          <g key={i}>
            <rect x={438} y={mOy + 78 + i * 58} width={444} height={52} rx={3}
              fill={i % 2 === 0 ? '#fafafa' : '#fff'} stroke="#ddd" strokeWidth="0.8"/>
            <text x={446} y={mOy + 93 + i * 58} fontSize={10.5} fill="#7B0D0D" fontWeight="700">{n} {title}</text>
            {body.split('\n').map((l, j) => (
              <text key={j} x={446} y={mOy + 108 + i * 58 + j * 14} fontSize={9} fill="#333">{l}</text>
            ))}
          </g>
        ))}

        {/* Lag compensator note */}
        <rect x={438} y={mOy + 370} width={444} height={0} rx={2}/>
      </svg>
      <div style={S.dCaption}>Lead compensator design: the uncompensated loop (red) has PM={PM_plant.toFixed(0)}° — adequate but the target is 45°. The lead compensator C=(s+1)/(s/8+1) adds a positive phase hump centred at √(1×8)≈2.8 rad/s (orange dashes). The compensated loop (green) achieves PM={PM_comp.toFixed(0)}° at a higher crossover frequency ωgc={gcComp.toFixed(1)} rad/s — faster and more stable. The five-step procedure systematically designs any lead compensator to meet a phase margin specification.</div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// Main Export
// ─────────────────────────────────────────────────────────────────
export default function BodePlots() {
  return (
    <div style={S.wrapper}>
      <h1 style={S.h1}>Control Systems — Bode Plots</h1>
      <p style={S.p}>
        The Bode plot is the most important graphical tool in frequency-domain control systems analysis. It displays two pieces of information about a transfer function G(jω) across a wide range of frequencies: the magnitude (how much the system amplifies or attenuates a sinusoidal input) plotted in decibels, and the phase (how much the output lags behind the input) plotted in degrees — both against a logarithmic frequency axis. The logarithmic scales are not just a convenience: they convert multiplications of transfer functions into additions, making it possible to construct the Bode plot of a complex system by simply summing the contributions of its individual poles, zeros, gains, and time delays. The Bode plot directly reveals stability margins, bandwidth, resonance, and disturbance rejection capability — making it the bridge between system design and frequency-domain specifications.
      </p>

      <InfoBox title="Core Concepts at a Glance" items={[
        "Bode plot = magnitude |G(jω)| in dB + phase ∠G(jω) in degrees, plotted vs log₁₀(ω)",
        "20 dB = gain of 10 | 6 dB ≈ gain of 2 | 0 dB = gain of 1 | −20 dB = gain of 0.1",
        "Log frequency axis: equal spacing per decade (0.1→1→10→100), compresses wide range",
        "Products → sums: |G₁G₂|dB = |G₁|dB + |G₂|dB  |  ∠G₁G₂ = ∠G₁ + ∠G₂",
        "First-order pole 1/(τs+1): −3 dB at ωc=1/τ, −20 dB/decade above, −45° phase at ωc",
        "Second-order: −40 dB/decade, −90° phase at ωₙ (all ζ), resonant peak for ζ < 0.707",
        "Phase margin PM: phase above −180° at gain crossover — target ≥ 45°",
        "Gain margin GM: gain below 0 dB at phase crossover — target ≥ 6 dB",
      ]}/>

      <h2 style={S.h2}>What Bode Plots Show: Sinusoidal Steady State</h2>
      <p style={S.p}>
        Every Bode plot ultimately describes the steady-state response to a sinusoidal input. If the input is A·sin(ωt), the output of a stable LTI system is |G(jω)|·A·sin(ωt + ∠G(jω)) — same frequency, scaled amplitude, shifted phase. The Bode plot records these two numbers, |G(jω)| in dB and ∠G(jω) in degrees, for every frequency ω from very slow to very fast. Understanding this physical meaning is the foundation for all subsequent Bode analysis.
      </p>
      <DiagramSinusoidalSteadyState/>
      <Table2 rows={[
        ["Frequency response definition","G(jω) = G(s)|_{s=jω}  (substitute s=jω into transfer function)\nValid only in sinusoidal steady state (transients died away)\nComplex number: G(jω) = Re{G} + j·Im{G}"],
        ["Magnitude |G(jω)|","Output amplitude / Input amplitude at frequency ω\n|G(jω)| = √(Re² + Im²)\nIn dB: 20·log₁₀|G(jω)|  — Bode magnitude plot"],
        ["Phase ∠G(jω)","Phase of output relative to input (negative = lag)\n∠G(jω) = atan2(Im{G}, Re{G})  in degrees or radians\nBode phase plot — negative for typical causal systems"],
        ["Physical interpretation","|G| > 1 (positive dB): output larger than input at this ω\n|G| < 1 (negative dB): output smaller than input — attenuation\n∠G < 0: output lags input — typical for stable systems"],
        ["Why log frequency scale","Biological/physical phenomena span many decades (0.001 to 1000 rad/s)\nLog scale gives equal visual space per decade\nStraight-line asymptotes become exact on log scale"],
        ["Why dB magnitude scale","Products become sums: 20 log|GC| = 20 log|G| + 20 log|C|\nSlopes become integer multiples of 20 dB/decade\nSimple hand-calculation via asymptotic construction"],
        ["Gain formula examples","2× amplitude = 6 dB  |  10× = 20 dB  |  100× = 40 dB\n0.5× = −6 dB  |  0.1× = −20 dB  |  √2 = 3 dB"],
        ["Frequency units","ω [rad/s]: natural for Laplace domain formulas\nf [Hz] = ω/(2π): preferred in audio/communication\nBode plots typically show ω (rad/s) on horizontal axis"],
      ]}/>

      <h2 style={S.h2}>Bode Axis Conventions</h2>
      <p style={S.p}>
        The Bode plot has two sub-plots sharing the same logarithmic frequency axis. The upper plot shows magnitude in decibels (dB) and the lower shows phase in degrees. Both plots use a logarithmic horizontal axis — one unit of spacing corresponds to one decade (factor of ten) in frequency. The −180° horizontal line on the phase plot is the critical stability boundary, and the 0 dB line on the magnitude plot marks unity gain.
      </p>
      <DiagramBodeAxes/>
      <Table2 rows={[
        ["Decibel (dB) scale","dB = 20·log₁₀(|G|)  for amplitude  |  dB = 10·log₁₀(power)\n0 dB: unity gain  |  +20 dB: factor 10  |  −20 dB: factor 0.1\nKey: negative dB = attenuation below unity"],
        ["One decade","Frequency range from ω to 10ω\nOn log axis: equal visual width for every decade\nSlope expressed as dB per decade (dB/dec)"],
        ["Log-log coordinates","Straight lines on Bode are powers of ω:\nSlope +20 dB/dec: proportional to ω  |  0 dB/dec: constant\n−20 dB/dec: proportional to 1/ω  |  ±40 for second order"],
        ["Phase conventions","Measured in degrees (°), counter-clockwise positive\nLagging phase: negative angles — standard for stable systems\n−180°: output exactly anti-phase with input"],
        ["−180° significance","Closed-loop oscillation condition: |GC|=1 AND ∠GC=−180°\nAt −180°: the feedback signal is in phase with the error\nThreshold for instability in minimum-phase systems"],
        ["0 dB significance","Unity gain crossover: |G(jωgc)| = 1\nDefines bandwidth of the closed-loop system approximately\nAbove this frequency: open loop attenuates error signals"],
        ["Reading magnitude","Find desired ω on horizontal axis; read up to curve; read dB on left\nConvert dB to ratio: ratio = 10^(dB/20)\nExample: −6 dB → ratio = 0.5 (half amplitude)"],
        ["Reading phase","Find desired ω; read up to phase curve; read degrees on left\nPhase at gain crossover → phase margin directly\nPhase at −180° crossing → phase crossover frequency"],
      ]}/>

      <h2 style={S.h2}>First-Order Pole and Zero</h2>
      <p style={S.p}>
        The first-order pole 1/(τs+1) and zero (τs+1) are the most fundamental Bode building blocks. Each has a single corner frequency ωc=1/τ where the behaviour transitions. The straight-line asymptote approximation (flat then ±20 dB/decade) is accurate to within 3 dB in magnitude and within 6° in phase everywhere, and forms the basis of hand-sketch Bode construction.
      </p>
      <DiagramFirstOrder/>
      <Table2 rows={[
        ["Corner frequency ωc","ωc = 1/τ  [rad/s] — the only parameter of a first-order factor\nAlso called break frequency or 3 dB frequency\nDetermines where the slope changes in the asymptote approximation"],
        ["Pole magnitude: 1/(τs+1)","Low ω (ω ≪ ωc): |G| ≈ 1 → 0 dB (flat)\nHigh ω (ω ≫ ωc): |G| ≈ 1/(τω) → −20 dB/dec slope\nExact at ωc: |G| = 1/√2 → −3 dB"],
        ["Pole phase: 1/(τs+1)","Low ω: ∠G → 0°  |  High ω: ∠G → −90°\n∠G = −arctan(τω) — exactly −45° at ωc\nAsymptote: linear from −5.7° at ωc/10 to −84.3° at 10ωc"],
        ["Zero magnitude: (τs+1)","Mirror image of pole: 0 dB for ω ≪ ωc, +20 dB/dec for ω ≫ ωc\nExact at ωc: |G| = √2 → +3 dB"],
        ["Zero phase: (τs+1)","Phase lead from 0° to +90°, exactly +45° at ωc\nMirror image of pole phase"],
        ["Asymptote accuracy","Magnitude: ≤3 dB error anywhere, exactly 3 dB at ωc\nPhase: ≤6° error anywhere (maximum at ωc/10 and 10ωc)\nSufficient for initial design; exact for verification"],
        ["Multiple poles","n poles at same ωc: 1/(τs+1)ⁿ\nMagnitude: −3n dB at ωc, slope −20n dB/dec above ωc\nPhase: −45n° at ωc, total −90n° at high ω"],
        ["Identifying τ from Bode","Find −3 dB point on measured Bode → ωc = 1/τ\nOr: find frequency where phase = −45° → ωc\nBoth methods give same result for first-order"],
      ]}/>

      <h2 style={S.h2}>Second-Order System Bode</h2>
      <p style={S.p}>
        The second-order denominator s²+2ζωₙs+ωₙ² introduces two poles and cannot be factored into two real first-order factors when ζ &lt; 1. Its Bode plot has a −40 dB/decade roll-off (like two first-order poles) but also a resonant peak near ωₙ for underdamped systems, and the universal property that phase passes through exactly −90° at ω=ωₙ for any damping ratio.
      </p>
      <DiagramSecondOrder/>
      <Table2 rows={[
        ["Standard form","G(s) = ωₙ²/(s²+2ζωₙs+ωₙ²)\nTwo parameters: ωₙ (natural frequency) and ζ (damping ratio)\nDC gain = 1 for this standard form"],
        ["Magnitude asymptotes","Low ω: 0 dB (flat)  |  High ω: −40 dB/decade (−40n·log₁₀(ω/ωₙ))\nBreak point at ω = ωₙ — the natural frequency\nNote: break is at ωₙ, not at any real pole location (poles are complex)"],
        ["Resonant peak Mr","Exists for ζ < 1/√2 ≈ 0.707\nMr = 1/(2ζ√(1−ζ²))  in ratio  |  Mr_dB = −20 log₁₀(2ζ√(1−ζ²))\nζ=0.1: +14 dB | ζ=0.3: +4.7 dB | ζ=0.5: +1.2 dB"],
        ["Resonant frequency ωᵣ","ωᵣ = ωₙ√(1−2ζ²)  (only for ζ < 0.707)\nSlightly below ωₙ — approaches ωₙ as ζ→0\nAt ζ=0.707: ωᵣ=0 (peak just disappears)"],
        ["Phase: universal −90° at ωₙ","∠G(jωₙ) = −atan2(2ζ·ωₙ²/ωₙ², 1−ωₙ²/ωₙ²) = −atan2(2ζ, 0) = −90°\nIndependent of ζ — holds for all damping ratios\nKey identification tool: measure phase crossover of −90° → find ωₙ"],
        ["Phase range","Varies from 0° (low ω) to −180° (high ω)\nSteeper transition for lower ζ — narrow band with large phase swing\nThis steep phase limits achievable bandwidth in feedback control"],
        ["Identifying ωₙ and ζ from Bode","ωₙ: frequency where phase = −90° (always)\nζ: from peak height Mr_dB = −20 log₁₀(2ζ√(1−ζ²)) if ζ<0.707\nOr from −3 dB bandwidth: BW = ωₙ√(1−2ζ²+√(4ζ⁴−4ζ²+2))"],
        ["Non-standard second order","(s²+2ζωₙs+ωₙ²) in numerator = complex conjugate zero\nSame magnitude shape but inverted: +40 dB/dec above ωₙ, phase +180°\nUsed in notch filters and resonance cancellation"],
      ]}/>

      <h2 style={S.h2}>Basic Bode Building Blocks</h2>
      <p style={S.p}>
        Every transfer function can be decomposed into a product of six types of building blocks: constant gain K, integrators (1/s)ⁿ, differentiators sⁿ, first-order poles, first-order zeros, and time delays e^(−Ls). The Bode magnitude is the sum of the individual magnitudes (in dB) and the phase is the sum of the individual phases. This additive decomposition is what makes the Bode plot so powerful for both analysis and design.
      </p>
      <DiagramBuildingBlocks/>
      <Table2 rows={[
        ["Constant gain K","Magnitude: 20·log₁₀(K) dB — horizontal line, no frequency dependence\nPhase: 0° for K>0, −180° for K<0\nSets the DC (low-frequency) level of the Bode plot"],
        ["Integrator 1/s","Magnitude: −20·log₁₀(ω) — passes through 0 dB at ω=1, slope −20 dB/dec\nPhase: −90° (constant at all frequencies)\nAdds −90° phase to the loop — reduces phase margin"],
        ["Differentiator s","Magnitude: +20·log₁₀(ω) — passes through 0 dB at ω=1, slope +20 dB/dec\nPhase: +90° (constant at all frequencies)\nBoosts high-frequency gain — used in D controller"],
        ["n integrators 1/sⁿ","Magnitude: −20n dB/decade, through 0 dB at ω=1\nPhase: −90n° (constant)\n1/s²: double integrator, −180° phase (destabilising!)"],
        ["Dead time e^{−Ls}","Magnitude: 0 dB at all frequencies (pure phase element)\nPhase: −L·ω·(180/π) degrees — grows unboundedly\nSevere phase lag at high ω limits achievable bandwidth"],
        ["Why dead time is challenging","As ω increases, phase grows without bound → −180° crossed at finite ω\nωgc × L product determines how much bandwidth loss occurs\nRule: L < 0.3 × (desired closed-loop TC) for tractable control"],
        ["Composition rule","|G₁G₂| (dB) = |G₁| (dB) + |G₂| (dB) at every ω\n∠(G₁G₂) = ∠G₁ + ∠G₂ at every ω\nEnabled by logarithm converting × to +"],
        ["Counting phase contributions","Type n plant (n integrators): contributes −90n° phase at all ω\nEach pole: contributes −90° additional phase at high ω\nEach zero: contributes +90° additional phase at high ω\nTotal high-ω phase: −90·(poles−zeros)°"],
      ]}/>

      <h2 style={S.h2}>Composite Bode Construction</h2>
      <p style={S.p}>
        Given an arbitrary rational transfer function, the Bode plot is constructed by: (1) writing the transfer function in factored time-constant form, (2) identifying all corner frequencies, (3) drawing the low-frequency asymptote from the DC gain and system type, then (4) breaking the slope at each corner frequency by ±20 dB/dec depending on whether it is a pole or zero. The phase is constructed similarly by summing the contributions from each element.
      </p>
      <DiagramCompositeBode/>
      <Table2 rows={[
        ["Factored form","Write G(s) = K·∏(s/ωzᵢ+1) / [sⁿ·∏(s/ωpⱼ+1)]\nK = total gain at s→0 (after removing integrators)\nEach factor corresponds to one corner frequency"],
        ["Step 1: Low-ω asymptote","Type n: start with slope −20n dB/decade\nAt ω=1: magnitude = 20·log₁₀(K)\nDraw this starting line from very low frequency"],
        ["Step 2: Break at each corner","At each pole ωp: slope decreases by 20 dB/dec\nAt each zero ωz: slope increases by 20 dB/dec\nMark each corner frequency on frequency axis"],
        ["Step 3: Phase construction","Start at −90·n° for type n\nAt each pole ωp: phase transitions −90° over one decade each side\nAt each zero ωz: phase transitions +90° over one decade each side"],
        ["Asymptote corrections","At each corner ωc: exact is ±3 dB vs asymptote (pole: −3, zero: +3)\nAt 10ωc and ωc/10: ±1 dB correction\nApply corrections only if accuracy beyond 3 dB is needed"],
        ["Total slope rule","Count (zeros − poles) in denominator so far at any frequency\nSlope = −20·(num_poles_so_far − num_zeros_so_far) dB/dec\nAsymptote slope must end at −20·(total poles − total zeros) dB/dec"],
        ["Total phase rule","High-ω phase = −90·(total poles − total zeros) degrees\nMinimum-phase systems: phase completely determined by magnitude slope\nNon-minimum phase (RHP zero/delay): extra phase lag beyond minimum"],
        ["Verification","Check DC gain: G(0) should match starting magnitude level\nCheck high-ω slope: should be −20·(p−z) dB/dec\nCheck phase limits: 0° at low ω, −90·(p−z)° at high ω"],
      ]}/>

      <h2 style={S.h2}>Gain Margin, Phase Margin &amp; Stability</h2>
      <p style={S.p}>
        The Bode plot provides the two fundamental stability margins of a feedback control system. Phase margin (PM) measures how close the open-loop phase is to −180° at the frequency where the gain equals unity. Gain margin (GM) measures how much the gain can increase before the loop becomes unstable at the phase crossover frequency. Both must be positive for stability (for minimum-phase systems), and typical design targets are PM ≥ 45° and GM ≥ 6 dB.
      </p>
      <DiagramStabilityMargins/>
      <Table2 rows={[
        ["Gain crossover ωgc","Frequency where |G(jω)C(jω)| = 1 (0 dB)\nDefines the approximate closed-loop bandwidth\nHigher ωgc → faster closed-loop response (but needs sufficient PM)"],
        ["Phase margin PM","PM = 180° + ∠GC(jωgc)\nRead: phase at gain crossover, add 180°\nPM > 0°: stable | PM = 0°: sustained oscillation | PM < 0°: unstable"],
        ["Phase crossover ωpc","Frequency where ∠GC(jω) = −180°\nIf GM > 0 dB: |GC(jωpc)| < 1 → stable\nIf GM < 0 dB: |GC(jωpc)| > 1 → unstable"],
        ["Gain margin GM","GM = −20·log₁₀|GC(jωpc)|  [dB]\nRead: negative of magnitude at phase crossover (in dB)\nGM > 0: stable | GM = 0: marginally stable | GM < 0: unstable"],
        ["Stability criterion (Bode)","Minimum-phase systems: stable ↔ PM > 0° AND GM > 0 dB\nNon-minimum-phase (RHP zeros or poles): need Nyquist criterion\nDead time is NOT minimum-phase — use Nyquist or Smith predictor"],
        ["Typical design targets","PM ≥ 45°: comfortable stability with moderate overshoot (~10%)\nPM ≥ 60°: conservative — overshoot ~1%\nGM ≥ 6 dB: gain can double before instability\nGM ≥ 12 dB: very conservative"],
        ["PM and transient response","PM ≈ 100·ζ degrees (for 0 < ζ < 0.7) — approximate\nPM = 45° → ζ ≈ 0.45, %OS ≈ 23%\nPM = 60° → ζ ≈ 0.6, %OS ≈ 10%"],
        ["Effect of gain increase","Higher K: magnitude plot shifts up → ωgc increases → PM decreases\nRule of thumb: PM decreases by ~(10 dB/20 dB/dec × phase slope) per dB of gain\nBode makes gain sensitivity to PM transparent"],
      ]}/>

      <h2 style={S.h2}>Compensator Design via Bode</h2>
      <p style={S.p}>
        The Bode plot is not just an analysis tool — it is the primary design canvas for frequency-domain compensator design. Lead compensators add a phase hump to increase phase margin at a chosen crossover frequency. Lag compensators reduce gain at high frequencies to increase phase margin by lowering the crossover. Lead-lag compensators provide both effects. The visual, graphical nature of Bode design makes it intuitive and transparent.
      </p>
      <DiagramCompensatorDesign/>
      <Table2 rows={[
        ["Lead compensator C_lead(s)","C(s) = (s/ωz+1)/(s/ωp+1),  ωp > ωz (zero is below pole)\nAdds positive phase lead between ωz and ωp\nMax phase at ωmax = √(ωz·ωp); magnitude +10·log₁₀(N) dB at ωmax"],
        ["Lead compensator design","1. Determine required PM increase Δφ\n2. φmax = Δφ + 5–10° safety margin\n3. N = (1+sinφmax)/(1−sinφmax)\n4. Place ωmax at new desired crossover frequency\n5. ωz = ωmax/√N,  ωp = ωmax·√N"],
        ["Lag compensator C_lag(s)","C(s) = (s/ωz+1)/(s/ωp+1),  ωp < ωz (pole is below zero)\nReduces magnitude above ωp without adding much phase at crossover\nUsed to reduce gain at high frequency, bringing ωgc to lower ω where PM is higher"],
        ["Lag design rule","Place lag corner frequencies well below desired ωgc: ωp, ωz ≪ ωgc\nOnly magnitude effect at ωgc — phase contribution is negligible\nGain attenuation of 20·log₁₀(ωz/ωp) dB applied above ωp"],
        ["Lead-lag compensator","Combine lead and lag: add phase at desired crossover AND reduce high-ω gain\nEquivalent to PID in many cases\nDesign lead part for PM | design lag part for attenuation requirement"],
        ["Notch filter (anti-resonance)","C(s) = (s²+2ζ₁ωₙs+ωₙ²)/(s²+2ζ₂ωₙs+ωₙ²),  ζ₂ >> ζ₁\nDeep notch at ωₙ in magnitude — cancels plant resonance\nUseful for flexible structures (robot arms, aircraft, hard drives)"],
        ["Loop shaping philosophy","Design |G(jω)C(jω)| to cross 0 dB with:  +20 dB/dec slope at crossover\nSteep slope (−40 dB/dec) at crossover → poor PM (avoid)\nShallow slope (−20 dB/dec) at crossover → good PM (target)"],
        ["Bandwidth and noise tradeoff","Higher bandwidth (ωgc): faster response but amplifies measurement noise\nLower bandwidth: less noise but slower response and worse disturbance rejection\nBode makes this trade-off quantitative and visible"],
      ]}/>

      <InfoBox title="Key Formulae Summary" items={[
        "Magnitude: 20·log₁₀|G(jω)| dB  |  Phase: atan2(Im{G(jω)}, Re{G(jω)}) degrees",
        "First-order pole 1/(τs+1): ωc=1/τ, −3 dB at ωc, −45° at ωc, −20 dB/dec above ωc",
        "Second-order: ωᵣ=ωₙ√(1−2ζ²), Mr=1/(2ζ√(1−ζ²)), phase = −90° at ωₙ for all ζ",
        "Composition: |G₁G₂|(dB) = |G₁|+|G₂|  |  ∠G₁G₂ = ∠G₁+∠G₂",
        "Phase margin: PM = 180° + ∠GC(jωgc)  at gain crossover (target ≥ 45°)",
        "Gain margin: GM = −20·log₁₀|GC(jωpc)| dB  at phase crossover (target ≥ 6 dB)",
        "Lead compensator: φmax = arcsin((N−1)/(N+1)), N=ωp/ωz, ωmax=√(ωz·ωp)",
        "Dead time: |e^{−jωL}|=0 dB, ∠e^{−jωL} = −Lω·(180/π)° — unbounded phase lag",
      ]}/>
    </div>
  );
}